import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import Layout from 'Layout';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import Chart from 'chart.js/auto';
import { AccountData, FilterState, ModalState, StageType } from './types';
import { D, STATUS_COLORS, STAGE_STYLE } from './mockData';
import { s, money, compact, formatDate, vp, statusOf, REACH, riskFromReport, lumirScore, RiskCalc } from './helpers';
import './style.scss';

import {
  fetchCapitalManagersDashboard,
  fetchCapitalManagers,
  fetchCapitalManagersDetail,
  fetchAccountTradeReport,
  DashboardData,
  TopManagerItem,
  CapitalManagersMeta,
  DetailAccountItem,
  StatDetailType,
  JourneyFunnel,
  NetworkStatus
} from './api';

import {
  HeroHeader,
  DashboardToolbar,
  FilterPanel,
  UnclassifiedAlert,
  KpiCards,
  JourneyNetworkSection,
  CapitalUnderManagement,
  PerformanceChart,
  KeyIndicators,
  TopManagersSection,
  ListDrilldownModal,
  PerformanceDetailModal,
  AccessManagementModal,
  StageClassifyModal,
  AccountDetailDrawer,
  ToastNotification,
  DrawerTileItem,
  DrawerRiskItem,
  DrawerCompItem
} from './component';

const TOP_PAGE_SIZE = 10;

const STAT_TYPE_MAPPING: Record<string, StatDetailType> = {
  total: 'total_cms',
  total_cms: 'total_cms',
  challenge: 'challenge',
  funded: 'funded',
  payout: 'payout',
  capital: 'total_cms',
  total_funded_capital: 'total_funded_capital',
  vpayout: 'payout',
  active: 'active_consistent',
  active_consistent: 'active_consistent',
  developing: 'developing',
  verification: 'verification',
  payout_achieved: 'payout_achieved',
  consistent: 'consistent',
};

const isoDate = (t: number) => new Date(t - new Date(t).getTimezoneOffset() * 6e4).toISOString().slice(0, 10);

const ManagerNetworkDashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const now = useMemo(() => Date.now(), []);

  const defaultF: FilterState = useMemo(() => ({
    network: 'All',
    range: '30d',
    platform: 'All',
    stage: 'All',
    status: 'All',
    from: isoDate(now - 30 * D),
    to: isoDate(now)
  }), [now]);

  const [accounts, setAccounts] = useState<AccountData[]>([]);
  const [accessTab, setAccessTab] = useState<'out' | 'in' | 'all'>('out');
  const [accessSearch, setAccessSearch] = useState('');
  const [accessSel, setAccessSel] = useState<Record<string, boolean>>({});
  const [accessExp, setAccessExp] = useState<Record<string, boolean>>({});
  const [f, setF] = useState<FilterState>(defaultF);
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('initialCapital');
  const [topSort, setTopSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'score', dir: 'desc' });
  const [perfTab, setPerfTab] = useState<'payout' | 'funded' | 'newcm'>('funded');
  const [modal, setModal] = useState<ModalState | null>(null);
  const [listSearch, setListSearch] = useState('');
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [drawerAccountNumber, setDrawerAccountNumber] = useState<string | null>(null);
  const [drawerAccountInfo, setDrawerAccountInfo] = useState<{
    cmName?: string;
    alias?: string;
    login?: string;
    platform?: string;
    stage?: string;
    capital?: number;
    capitalFormatted?: string;
    balance?: number;
    drawdown?: number;
    discipline?: number;
    risk?: number | null;
    consistency?: number;
    score?: number;
  } | null>(null);
  const [drawerReport, setDrawerReport] = useState<any | null>(null);
  const [loadingDrawerReport, setLoadingDrawerReport] = useState<boolean>(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [lastUpdated, setLastUpdated] = useState(now);
  const [refreshing, setRefreshing] = useState(false);
  const [tip, setTip] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [w, setW] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1440));
  const [mw, setMw] = useState(() => (typeof window !== 'undefined' ? Math.max(300, window.innerWidth - 64) : 1000));

  // API State
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [topManagersApiData, setTopManagersApiData] = useState<TopManagerItem[] | null>(null);
  const [topManagersMeta, setTopManagersMeta] = useState<CapitalManagersMeta | null>(null);
  const [loadingDashboard, setLoadingDashboard] = useState<boolean>(true);
  const [loadingTopManagers, setLoadingTopManagers] = useState<boolean>(true);
  const [selectedTierCapital, setSelectedTierCapital] = useState<number | null>(null);

  // Stats detail modal state
  const [statDetailAccounts, setStatDetailAccounts] = useState<DetailAccountItem[]>([]);
  const [statDetailTotal, setStatDetailTotal] = useState<number>(0);
  const [statDetailLastPage, setStatDetailLastPage] = useState<number>(1);
  const [statPage, setStatPage] = useState<number>(1);
  const [statSortBy, setStatSortBy] = useState<string>('initial_balance');
  const [statOrder, setStatOrder] = useState<'desc' | 'asc'>('desc');
  const [statSearch, setStatSearch] = useState<string>('');
  const [statLoading, setStatLoading] = useState<boolean>(false);

  // Number of accounts whose stage is Funded; caps the Funded-only metrics the dashboard API over-reports
  const [fundedAccounts, setFundedAccounts] = useState<number | null>(null);
  // Status of every tracked account, counted from the account list (the dashboard's network_status over-counts)
  const [accountStatus, setAccountStatus] = useState<{ total: number; counts: Record<string, number> } | null>(null);

  const loadDashboardData = useCallback(async () => {
    setLoadingDashboard(true);
    try {
      const [data, fundedRes, allRes] = await Promise.all([
        fetchCapitalManagersDashboard(),
        fetchCapitalManagersDetail({ type: 'funded', page: 1, per_page: 1 }),
        fetchCapitalManagersDetail({ type: 'total_cms', page: 1, per_page: 100 })
      ]);
      setDashboardData(data);
      setFundedAccounts(fundedRes.success ? Number(fundedRes.meta?.total_accounts ?? fundedRes.data.length) : null);
      const allTotal = Number(allRes.meta?.total_accounts ?? allRes.data.length);
      if (allRes.success && allRes.data.length === allTotal) {
        const norm = (v: string) => String(v || '').toLowerCase().replace(/[\s_-]/g, '');
        const keyOf: Record<string, string> = { normal: 'Normal', attention: 'Attention', paused: 'Paused', recovery: 'Recovery', scaleready: 'Scale Ready' };
        const counts: Record<string, number> = {};
        allRes.data.forEach(a => {
          const k = keyOf[norm(a.status)] || 'Normal';
          counts[k] = (counts[k] || 0) + 1;
        });
        setAccountStatus({ total: allTotal, counts });
      } else {
        setAccountStatus(null);
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoadingDashboard(false);
    }
  }, []);

  // Search box is applied server-side (name / account number); debounced so typing doesn't spam the API
  const [topQuery, setTopQuery] = useState('');
  const topReqIdRef = useRef<number>(0);

  // How many ranked rows are shown; "Show more" reveals 10 more
  const [topVisible, setTopVisible] = useState(TOP_PAGE_SIZE);

  // The whole list is loaded so the ranking (which uses our own risk score) covers every CM, not just the first page
  const loadTopManagersData = useCallback(async () => {
    const reqId = ++topReqIdRef.current;
    setLoadingTopManagers(true);
    setTopVisible(TOP_PAGE_SIZE);
    try {
      const params = {
        per_page: 100,
        search: topQuery || undefined,
        capital: selectedTierCapital !== null ? selectedTierCapital : undefined
      };
      const first = await fetchCapitalManagers({ ...params, page: 1 });
      let list = first.data || [];
      const lastPage = Math.min(first.meta?.last_page || 1, 10);
      for (let p = 2; p <= lastPage; p++) {
        if (reqId !== topReqIdRef.current) return;
        const next = await fetchCapitalManagers({ ...params, page: p });
        list = list.concat(next.data || []);
      }
      if (reqId !== topReqIdRef.current) return;
      setTopManagersApiData(list);
      setTopManagersMeta({
        total_cms: first.meta?.total_cms ?? list.length,
        current_page: lastPage,
        per_page: params.per_page,
        last_page: lastPage
      });
    } catch (err) {
      console.error('Failed to load top managers data:', err);
    } finally {
      if (reqId === topReqIdRef.current) setLoadingTopManagers(false);
    }
  }, [topQuery, selectedTierCapital]);

  // Risk score per account number, computed from each account's trade report (null = no trades yet)
  const [riskByAccount, setRiskByAccount] = useState<Record<string, RiskCalc | null>>({});
  const riskInFlightRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const pending = (topManagersApiData || [])
      .map(item => String(item.account_number || ''))
      .filter(acc => acc && !(acc in riskByAccount) && !riskInFlightRef.current.has(acc));
    if (pending.length === 0) return;
    pending.forEach(acc => riskInFlightRef.current.add(acc));
    let cursor = 0;
    const worker = async () => {
      while (cursor < pending.length) {
        const acc = pending[cursor++];
        let calc: RiskCalc | null = null;
        try {
          calc = riskFromReport(await fetchAccountTradeReport(acc));
        } catch (err) {
          calc = null;
        }
        riskInFlightRef.current.delete(acc);
        setRiskByAccount(prev => ({ ...prev, [acc]: calc }));
      }
    };
    // A few reports at a time so a large network doesn't flood the API
    Promise.all(Array.from({ length: Math.min(4, pending.length) }, worker));
  }, [topManagersApiData, riskByAccount]);

  const onSelectTierCapital = useCallback((cap: number) => {
    setSelectedTierCapital(prev => (prev === cap ? null : cap));
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Re-runs on mount and whenever the search or the capital tier changes
  useEffect(() => {
    loadTopManagersData();
  }, [loadTopManagersData]);

  // Fetch trade report when drawer opens
  useEffect(() => {
    if (!drawerId) {
      setDrawerReport(null);
      setLoadingDrawerReport(false);
      return;
    }

    const matchedAccount = accounts.find(x => x.accountId === drawerId) ||
      accounts.find(x => x.login === drawerAccountNumber);
    const targetAccountNum = drawerAccountInfo?.login ||
      drawerAccountNumber ||
      matchedAccount?.login ||
      (/^\d+$/.test(drawerId) ? drawerId : '17217317');

    let isMounted = true;
    const loadReport = async () => {
      setLoadingDrawerReport(true);
      try {
        const today = new Date().toISOString().split('T')[0];
        const res = await fetchAccountTradeReport(targetAccountNum, today);
        if (isMounted) {
          setDrawerReport(res);
        }
      } catch (err) {
        console.warn('Failed to load account trade report:', err);
      } finally {
        if (isMounted) {
          setLoadingDrawerReport(false);
        }
      }
    };

    loadReport();

    return () => {
      isMounted = false;
    };
  }, [drawerId, drawerAccountNumber, drawerAccountInfo, accounts]);

  const mainRef = useRef<HTMLDivElement>(null);
  const perfRef = useRef<HTMLCanvasElement>(null);
  const drawerRef = useRef<HTMLCanvasElement>(null);
  const perfChartRef = useRef<any>(null);
  const drawerChartRef = useRef<any>(null);
  const toastTimeoutRef = useRef<any>(null);
  const statReqIdRef = useRef<number>(0);

  const toastMsg = useCallback((msg: string) => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast(msg);
    toastTimeoutRef.current = setTimeout(() => setToast(null), 2600);
  }, []);

  // Window resize & container resize measurement
  useEffect(() => {
    const handleResize = () => {
      setW(window.innerWidth);
      if (mainRef.current) {
        setMw(Math.round(mainRef.current.getBoundingClientRect().width));
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();

    let ro: ResizeObserver | null = null;
    if (window.ResizeObserver && mainRef.current) {
      ro = new ResizeObserver(entries => {
        if (entries[0]) {
          const width = Math.round(entries[0].contentRect.width);
          if (width) setMw(width);
        }
      });
      ro.observe(mainRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (ro) ro.disconnect();
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  // Compute period
  const period = useMemo(() => {
    const d = new Date(now);
    const sod = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    switch (f.range) {
      case 'today': return { start: sod, end: now };
      case '7d': return { start: now - 7 * D, end: now };
      case 'month': return { start: new Date(d.getFullYear(), d.getMonth(), 1).getTime(), end: now };
      case 'lastmonth': return {
        start: new Date(d.getFullYear(), d.getMonth() - 1, 1).getTime(),
        end: new Date(d.getFullYear(), d.getMonth(), 1).getTime() - 1
      };
      case 'custom': {
        const s = Date.parse(f.from + 'T00:00:00');
        const e = Date.parse(f.to + 'T23:59:59');
        return { start: isNaN(s) ? now - 30 * D : s, end: isNaN(e) ? now : Math.min(e, now) };
      }
      default: return { start: now - 30 * D, end: now };
    }
  }, [now, f.range, f.from, f.to]);

  // Filtered accounts
  const filteredSet = useMemo(() => {
    return accounts.filter(a =>
      a.approved &&
      a.joinDate <= period.end &&
      (f.network === 'All' || a.network === f.network) &&
      (f.platform === 'All' || a.platform === f.platform) &&
      (f.stage === 'All' || a.stage === f.stage) &&
      (f.status === 'All' || statusOf(a) === f.status)
    );
  }, [accounts, period.end, f.network, f.platform, f.stage, f.status]);

  const prevSet = useMemo(() => {
    return filteredSet.filter(a => a.joinDate < period.start);
  }, [filteredSet, period.start]);

  const computeMetrics = useCallback((set: AccountData[], end: number) => {
    const cls = set.filter(a => a.stage !== 'Unclassified');
    const funded = cls.filter(a => a.stage === 'Funded');
    const avg = (arr: AccountData[], fn: (a: AccountData) => number) =>
      arr.length ? arr.reduce((s, a) => s + fn(a), 0) / arr.length : 0;
    const reach: Record<string, number> = {};
    Object.keys(REACH).forEach(k => reach[k] = cls.filter(a => REACH[k](a, end)).length);
    const status: Record<string, number> = {};
    Object.keys(STATUS_COLORS).forEach(k => status[k] = 0);
    cls.forEach(a => status[statusOf(a)]++);
    const good = funded.filter(a => ['Normal', 'Scale Ready'].includes(statusOf(a)) && a.lastTradeAgo <= 7).length;
    return {
      total: cls.length,
      reach,
      status,
      funded: funded.length,
      fundedCapital: funded.reduce((s, a) => s + a.initialCapital, 0),
      verifiedPayout: cls.reduce((s, a) => s + vp(a, end).reduce((x, p) => x + p.amount, 0), 0),
      activePct: funded.length ? (good / funded.length) * 100 : 0,
      activeFunded: funded.filter(a => !a.paused && a.lastTradeAgo <= 7).length,
      consistency: avg(funded, a => a.consistencyScore),
      avgDD: avg(funded, a => a.drawdown),
      sysComp: avg(cls, a => a.systemCompliance),
      riskComp: avg(cls, a => a.riskCompliance)
    };
  }, []);

  const cur = useMemo(() => computeMetrics(filteredSet, period.end), [computeMetrics, filteredSet, period.end]);
  const prev = useMemo(() => computeMetrics(prevSet, period.start), [computeMetrics, prevSet, period.start]);

  const unclassifiedAll = useMemo(() => accounts.filter(a => a.approved && a.stage === 'Unclassified'), [accounts]);
  const unclassifiedCount = unclassifiedAll.length;

  const matchesSearch = useCallback((a: AccountData, q: string) => {
    if (!q) return true;
    const s = q.trim().toLowerCase();
    return a.accountId.toLowerCase().includes(s) ||
      a.cmName.toLowerCase().includes(s) ||
      a.platform.toLowerCase().includes(s) ||
      a.alias.toLowerCase().includes(s) ||
      a.login.includes(s);
  }, []);

  // Actions
  const togglePause = useCallback((id: string) => {
    let nowState: boolean;
    setAccounts(prev => prev.map(a => {
      if (a.accountId !== id) return a;
      nowState = !a.paused;
      return { ...a, paused: nowState, lastTradeAgo: nowState ? a.lastTradeAgo : Math.min(a.lastTradeAgo, 3) };
    }));
    toastMsg(nowState!
      ? t('managerNetwork.toasts.monitoringPaused', 'Monitoring paused for {{id}}', { id })
      : t('managerNetwork.toasts.monitoringResumed', 'Monitoring resumed for {{id}}', { id })
    );
  }, [toastMsg, t]);

  const loadStatDetail = useCallback(async (type: StatDetailType, sTerm: string, sb: string, ord: 'desc' | 'asc', p: number) => {
    const reqId = ++statReqIdRef.current;
    setStatLoading(true);
    try {
      const res = await fetchCapitalManagersDetail({
        type,
        search: sTerm,
        sort_by: sb,
        order: ord,
        page: p,
        per_page: 10
      });
      if (reqId === statReqIdRef.current) {
        setStatDetailAccounts(res.data || []);
        setStatDetailTotal(res.meta?.total_accounts ?? (res.data ? res.data.length : 0));
        setStatDetailLastPage(res.meta?.last_page || 1);
      }
    } catch (err) {
      if (reqId === statReqIdRef.current) {
        console.error('Error fetching stat detail accounts:', err);
      }
    } finally {
      if (reqId === statReqIdRef.current) {
        setStatLoading(false);
      }
    }
  }, []);

  const openStatList = useCallback((id: string, title: string, sub: string, knownCount?: number) => {
    const apiType = STAT_TYPE_MAPPING[id] || 'total_cms';
    setStatDetailAccounts([]);
    setStatDetailTotal(0);
    setStatSearch('');
    setStatPage(1);
    setStatSortBy('initial_balance');
    setStatOrder('desc');
    setModal({ type: 'list', title, sub, key: 'stat:' + id, empty: knownCount === 0 });
    // The detail API ignores types it can't resolve (payout_achieved, consistent…) and returns every account
    if (knownCount === 0) {
      statReqIdRef.current++;
      setStatDetailLastPage(1);
      setStatLoading(false);
      return;
    }
    setStatLoading(true);
    loadStatDetail(apiType, '', 'initial_balance', 'desc', 1);
  }, [loadStatDetail]);

  const onStatSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setStatSearch(val);
    setStatPage(1);
    if (modal && modal.key && modal.key.startsWith('stat:') && !modal.empty) {
      const id = modal.key.replace('stat:', '');
      const apiType = STAT_TYPE_MAPPING[id] || 'total_cms';
      loadStatDetail(apiType, val, statSortBy, statOrder, 1);
    }
  }, [modal, statSortBy, statOrder, loadStatDetail]);

  const onStatSortByChange = useCallback((newSortBy: string) => {
    setStatSortBy(newSortBy);
    setStatPage(1);
    if (modal && modal.key && modal.key.startsWith('stat:') && !modal.empty) {
      const id = modal.key.replace('stat:', '');
      const apiType = STAT_TYPE_MAPPING[id] || 'total_cms';
      setStatDetailAccounts([]);
      loadStatDetail(apiType, statSearch, newSortBy, statOrder, 1);
    }
  }, [modal, statSearch, statOrder, loadStatDetail]);

  const onStatOrderToggle = useCallback(() => {
    const newOrder = statOrder === 'desc' ? 'asc' : 'desc';
    setStatOrder(newOrder);
    setStatPage(1);
    if (modal && modal.key && modal.key.startsWith('stat:') && !modal.empty) {
      const id = modal.key.replace('stat:', '');
      const apiType = STAT_TYPE_MAPPING[id] || 'total_cms';
      setStatDetailAccounts([]);
      loadStatDetail(apiType, statSearch, statSortBy, newOrder, 1);
    }
  }, [modal, statSearch, statSortBy, statOrder, loadStatDetail]);

  const onStatPageChange = useCallback((newPage: number) => {
    setStatPage(newPage);
    if (modal && modal.key && modal.key.startsWith('stat:') && !modal.empty) {
      const id = modal.key.replace('stat:', '');
      const apiType = STAT_TYPE_MAPPING[id] || 'total_cms';
      setStatDetailAccounts([]);
      loadStatDetail(apiType, statSearch, statSortBy, statOrder, newPage);
    }
  }, [modal, statSearch, statSortBy, statOrder, loadStatDetail]);

  const openList = useCallback((title: string, sub: string, key: string, sort?: string | null) => {
    setStatDetailAccounts([]);
    setStatDetailTotal(0);
    setStatLoading(false);
    statReqIdRef.current++;
    setModal({ type: 'list', title, sub, key, sort: sort || null });
    setListSearch('');
  }, []);

  // Predicate for drill-down
  const getPred = useCallback((key: string, end: number) => {
    const [k, v] = key.split(':');
    const cls = (a: AccountData) => a.stage !== 'Unclassified';
    const F = (a: AccountData) => a.stage === 'Funded';
    if (k === 'journey') return (a: AccountData) => cls(a) && REACH[v](a, end);
    if (k === 'status') return (a: AccountData) => cls(a) && statusOf(a) === v;
    if (k === 'tier') return (a: AccountData) => F(a) && a.initialCapital === +v;
    if (k === 'unclassified') return (a: AccountData) => !cls(a);
    if (k === 'kpi') {
      if (v === 'total') return cls;
      if (v === 'funded') return F;
      if (v === 'vpayout') return (a: AccountData) => cls(a) && vp(a, end).length > 0;
      if (v === 'active') return (a: AccountData) => F(a) && !a.paused && a.lastTradeAgo <= 7;
      if (v === 'activeGood') return (a: AccountData) => F(a) && ['Normal', 'Scale Ready'].includes(statusOf(a)) && a.lastTradeAgo <= 7;
      if (v === 'cls') return cls;
    }
    return () => true;
  }, []);

  // Performance chart data
  const perfData = useMemo(() => {
    const num = (v: number) => String(Math.round(v));
    const mk = (v: number, t?: boolean) => (t ? compact(v).replace('.0K', 'K') : money(v));

    if (dashboardData?.performance_over_time) {
      const pot = dashboardData.performance_over_time;
      if (perfTab === 'payout') {
        const labels = pot.payout.map(x => x.label);
        const bar = pot.payout.map(x => x.verified_payout);
        const line = pot.payout.map(x => x.cm_payout_count);
        const ser = pot.payout.map((x, i) => ({
          label: x.label,
          newCM: pot.new_cm[i]?.new_capital_managers || 0,
          growth: pot.new_cm[i]?.growth_percent || 0,
          newFunded: pot.funded_cm[i]?.new_funded_cm || 0,
          totalFunded: pot.funded_cm[i]?.total_funded_cm || 0,
          fundedCap: 0,
          payout: x.verified_payout,
          paid: x.cm_payout_count
        }));
        return {
          barLabel: t('managerNetwork.perf.verifiedPayout', 'Verified Payout ($)'),
          lineLabel: t('managerNetwork.perf.payoutCMCount', 'CMs with payout'),
          bar,
          line,
          barFmt: mk,
          lineFmt: num,
          extra: undefined as any,
          labels,
          ser
        };
      } else if (perfTab === 'funded') {
        const labels = pot.funded_cm.map(x => x.label);
        const bar = pot.funded_cm.map(x => x.new_funded_cm);
        const line = pot.funded_cm.map(x => x.total_funded_cm);
        const ser = pot.funded_cm.map((x, i) => ({
          label: x.label,
          newCM: pot.new_cm[i]?.new_capital_managers || 0,
          growth: pot.new_cm[i]?.growth_percent || 0,
          newFunded: x.new_funded_cm,
          totalFunded: x.total_funded_cm,
          fundedCap: 0,
          payout: pot.payout[i]?.verified_payout || 0,
          paid: pot.payout[i]?.cm_payout_count || 0
        }));
        return {
          barLabel: t('managerNetwork.perf.newFundedCm', 'New funded CM'),
          lineLabel: t('managerNetwork.perf.totalFundedCm', 'Total funded CM'),
          bar,
          line,
          barFmt: num,
          lineFmt: num,
          extra: (i: number) => `${t('managerNetwork.perf.totalFundedCm', 'Total funded CM')}: ${line[i]} CMs`,
          labels,
          ser
        };
      } else {
        const labels = pot.new_cm.map(x => x.label);
        const bar = pot.new_cm.map(x => x.new_capital_managers);
        const line = pot.new_cm.map(x => x.growth_percent);
        const ser = pot.new_cm.map((x, i) => ({
          label: x.label,
          newCM: x.new_capital_managers,
          growth: x.growth_percent,
          newFunded: pot.funded_cm[i]?.new_funded_cm || 0,
          totalFunded: pot.funded_cm[i]?.total_funded_cm || 0,
          fundedCap: 0,
          payout: pot.payout[i]?.verified_payout || 0,
          paid: pot.payout[i]?.cm_payout_count || 0
        }));
        return {
          barLabel: t('managerNetwork.perf.newCapitalManagers', 'New Capital Managers'),
          lineLabel: t('managerNetwork.perf.growthPercent', 'Growth %'),
          bar,
          line,
          barFmt: num,
          lineFmt: (v: number, t?: boolean) => (t ? Math.round(v) : v) + '%',
          extra: undefined as any,
          labels,
          ser
        };
      }
    }

    const cls = accounts.filter(a => a.approved && a.stage !== 'Unclassified');
    const months: { label: string; s: number; e: number }[] = [];
    const baseDate = new Date(now);
    for (let i = 5; i >= 0; i--) {
      const d = new Date(baseDate.getFullYear(), baseDate.getMonth() - i, 1);
      const s = d.getTime();
      const e = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59).getTime();
      months.push({ label: d.toLocaleDateString(i18n.language === 'vi' ? 'vi-VN' : 'en-US', { month: 'short' }), s, e });
    }
    const ser = months.map(m => {
      const newCM = cls.filter(a => a.joinDate >= m.s && a.joinDate <= m.e).length;
      const before = cls.filter(a => a.joinDate < m.s).length;
      const fd = cls.filter(a => a.fundedDate && a.fundedDate <= m.e);
      let payout = 0, paid = 0;
      cls.forEach(a => {
        const ps = a.payouts.filter(x => x.verified && x.date >= m.s && x.date <= m.e);
        if (ps.length) {
          paid++;
          payout += ps.reduce((t, x) => t + x.amount, 0);
        }
      });
      return {
        label: m.label,
        newCM,
        growth: before ? (newCM / before) * 100 : 0,
        newFunded: fd.filter(a => a.fundedDate! >= m.s).length,
        totalFunded: fd.length,
        fundedCap: fd.reduce((t, a) => t + a.initialCapital, 0),
        payout,
        paid
      };
    });
    const labels = ser.map(x => x.label);
    const P = {
      payout: { barLabel: t('managerNetwork.perf.verifiedPayout', 'Verified Payout ($)'), lineLabel: t('managerNetwork.perf.payoutCMCount', 'CMs with payout'), bar: ser.map(x => x.payout), line: ser.map(x => x.paid), barFmt: mk, lineFmt: num, extra: undefined as any },
      funded: { barLabel: t('managerNetwork.perf.newFundedCm', 'New funded CM'), lineLabel: t('managerNetwork.perf.totalFundedCm', 'Total funded CM'), bar: ser.map(x => x.newFunded), line: ser.map(x => x.totalFunded), barFmt: num, lineFmt: num, extra: (i: number) => `${t('managerNetwork.cum.totalFundedCapital', 'Funded capital')}: ` + money(ser[i].fundedCap) },
      newcm: { barLabel: t('managerNetwork.perf.newCapitalManagers', 'New Capital Managers'), lineLabel: t('managerNetwork.perf.growthPercent', 'Growth %'), bar: ser.map(x => x.newCM), line: ser.map(x => Math.round(x.growth * 10) / 10), barFmt: num, lineFmt: (v: number, t?: boolean) => (t ? Math.round(v) : v) + '%', extra: undefined as any }
    }[perfTab];
    return { ...P, labels, ser };
  }, [dashboardData, accounts, now, perfTab, t, i18n.language]);

  // Render Charts in useEffect
  useEffect(() => {
    const el = perfRef.current;
    if (!el || !perfData) return;
    if (perfChartRef.current) {
      perfChartRef.current.destroy();
    }
    const tick = { font: { size: 11 }, color: '#6B7280' };
    perfChartRef.current = new Chart(el, {
      data: {
        labels: perfData.labels,
        datasets: [
          { type: 'bar', label: perfData.barLabel, data: perfData.bar, backgroundColor: '#D9B574', hoverBackgroundColor: '#B8893A', borderRadius: 2, maxBarThickness: 30, yAxisID: 'y', order: 2 },
          { type: 'line', label: perfData.lineLabel, data: perfData.line, borderColor: '#1B1B1B', backgroundColor: '#1B1B1B', pointRadius: 3.5, pointHoverRadius: 5, tension: 0, borderWidth: 1.5, yAxisID: 'y1', order: 1 }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#01253C',
            padding: 10,
            callbacks: {
              label: (c: any) => ' ' + c.dataset.label + ': ' + (c.datasetIndex === 0 ? perfData.barFmt(c.raw) : perfData.lineFmt(c.raw)),
              footer: (it: any) => (perfData.extra ? perfData.extra(it[0].dataIndex) : '')
            }
          }
        },
        scales: {
          x: { grid: { display: false }, ticks: tick },
          y: { beginAtZero: true, grid: { color: '#EEF0F3' }, border: { display: false }, ticks: { ...tick, callback: (v: any) => perfData.barFmt(v, true) } },
          y1: { beginAtZero: true, position: 'right', grid: { display: false }, border: { display: false }, ticks: { ...tick, callback: (v: any) => perfData.lineFmt(v, true) } }
        },
        onClick: (_: any, els: any[]) => setModal({ type: 'perf', month: els.length ? els[0].index : null })
      }
    });
    return () => {
      if (perfChartRef.current) {
        perfChartRef.current.destroy();
        perfChartRef.current = null;
      }
    };
  }, [perfData, perfTab]);

  const drawerChartInfo = useMemo(() => {
    if (!drawerId) return { pts: [], labels: [], hasData: false, summary: null };
    const a = accounts.find(x => x.accountId === drawerId) ||
      accounts.find(x => x.login === drawerAccountNumber);

    const pts: number[] = [];
    const labels: string[] = [];

    const rep = drawerReport?.data?.data || drawerReport?.data || drawerReport;

    const parseDateLabel = (dateVal: any, fallback: string) => {
      if (!dateVal) return fallback;
      const parsed = new Date(dateVal);
      if (!isNaN(parsed.getTime())) {
        return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
      }
      return String(dateVal);
    };

    const initCap = Number(rep?.account_info?.initial_balance) ||
      Number(rep?.initial_balance) ||
      (drawerAccountInfo?.capital ? Number(drawerAccountInfo.capital) : 0) ||
      (a?.initialCapital ?? 0) ||
      0;

    // Ưu tiên dùng raw_trades từ report theo yêu cầu của user
    const rawTradesList = (
      (Array.isArray(rep?.raw_trades) && rep.raw_trades.length > 0 ? rep.raw_trades : null) ||
      (Array.isArray(rep?.journal_closed_trades) && rep.journal_closed_trades.length > 0 ? rep.journal_closed_trades : null) ||
      (Array.isArray(rep?.closed_trades) && rep.closed_trades.length > 0 ? rep.closed_trades : null) ||
      (Array.isArray(rep?.trades) && rep.trades.length > 0 ? rep.trades : null)
    );

    if (rawTradesList && rawTradesList.length > 0) {
      // Sắp xếp các lệnh theo thứ tự thời gian tăng dần
      const sortedTrades = [...rawTradesList].sort((t1: any, t2: any) => {
        const time1 = new Date(t1?.close_time || t1?.closeTime || t1?.open_time || t1?.opentime || t1?.time || t1?.date || 0).getTime();
        const time2 = new Date(t2?.close_time || t2?.closeTime || t2?.open_time || t2?.opentime || t2?.time || t2?.date || 0).getTime();
        return time1 - time2;
      });

      // Thêm điểm xuất phát (Initial Capital) nếu có
      if (initCap > 0) {
        pts.push(initCap);
        const firstTradeOpen = sortedTrades[0]?.open_time || sortedTrades[0]?.opentime || sortedTrades[0]?.close_time;
        labels.push(firstTradeOpen ? parseDateLabel(firstTradeOpen, t('managerNetwork.drawer.chartStart', 'Bắt đầu')) : t('managerNetwork.drawer.chartStart', 'Bắt đầu'));
      }

      let runningBalance = initCap;
      sortedTrades.forEach((trade: any, idx: number) => {
        let bal = trade?.balance_after !== undefined && trade?.balance_after !== null && !isNaN(Number(trade?.balance_after))
          ? Number(trade.balance_after)
          : null;

        if (bal === null) {
          const profit = Number(trade?.net_profit ?? trade?.profit ?? trade?.profit_gross ?? trade?.pnl ?? 0);
          runningBalance += profit;
          bal = Math.round(runningBalance * 100) / 100;
        } else {
          runningBalance = bal;
        }

        const tradeTime = trade?.close_time || trade?.closeTime || trade?.open_time || trade?.opentime || trade?.time || trade?.date;
        pts.push(bal);
        labels.push(parseDateLabel(tradeTime, `#${idx + 1}`));
      });
    } else if (rep?.timeseries_by_trades && Array.isArray(rep.timeseries_by_trades) && rep.timeseries_by_trades.length > 0) {
      if (initCap > 0 && Number(rep.timeseries_by_trades[0]?.y) !== initCap) {
        pts.push(initCap);
        labels.push(t('managerNetwork.drawer.chartStart', 'Bắt đầu'));
      }
      rep.timeseries_by_trades.forEach((item: any, idx: number) => {
        const val = Number(item.y);
        if (!isNaN(val)) {
          pts.push(val);
          labels.push(parseDateLabel(item.x, `#${idx + 1}`));
        }
      });
    } else if (rep?.daily_summary && Array.isArray(rep.daily_summary) && rep.daily_summary.length > 0) {
      let running = initCap;
      if (initCap > 0) {
        pts.push(initCap);
        labels.push(parseDateLabel(rep.daily_summary[0]?.date, t('managerNetwork.drawer.chartStart', 'Bắt đầu')));
      }
      rep.daily_summary.forEach((item: any, idx: number) => {
        if (item.balance !== undefined && !isNaN(Number(item.balance))) {
          pts.push(Number(item.balance));
        } else {
          running += Number(item.result ?? item.profit ?? 0);
          pts.push(Math.round(running * 100) / 100);
        }
        labels.push(parseDateLabel(item.date, `#${idx + 1}`));
      });
    } else if (rep?.equity_analysis?.equity_curve && Array.isArray(rep.equity_analysis.equity_curve) && rep.equity_analysis.equity_curve.length > 0) {
      rep.equity_analysis.equity_curve.forEach((item: any, idx: number) => {
        const val = Number(item.balance ?? item.equity ?? 0);
        if (!isNaN(val)) {
          pts.push(val);
          labels.push(parseDateLabel(item.date, `#${idx + 1}`));
        }
      });
    } else if (rep?.equity_curves && Array.isArray(rep.equity_curves) && rep.equity_curves.length > 0) {
      rep.equity_curves.forEach((item: any, idx: number) => {
        const val = Number(item.equity ?? item.balance ?? item.y ?? 0);
        if (!isNaN(val)) {
          pts.push(val);
          labels.push(parseDateLabel(item.date || item.x, `#${idx + 1}`));
        }
      });
    }

    // Nếu chỉ có 1 điểm dữ liệu, thêm vốn ban đầu làm điểm xuất phát nếu có
    if (pts.length === 1 && initCap > 0 && initCap !== pts[0]) {
      pts.unshift(initCap);
      labels.unshift(t('managerNetwork.drawer.chartStart', 'Bắt đầu'));
    }

    const hasData = pts.length > 0;
    let summary: { firstPt: number; lastPt: number; diff: number; diffPct: number; up: boolean } | null = null;
    if (hasData) {
      const firstPt = pts[0];
      const lastPt = pts[pts.length - 1];
      const diff = lastPt - firstPt;
      const diffPct = firstPt !== 0 ? (diff / Math.abs(firstPt)) * 100 : 0;
      summary = {
        firstPt,
        lastPt,
        diff,
        diffPct,
        up: lastPt >= firstPt
      };
    }

    return { pts, labels, hasData, summary };
  }, [drawerId, drawerAccountNumber, drawerReport, drawerAccountInfo, accounts, t]);

  useEffect(() => {
    if (drawerChartRef.current) {
      drawerChartRef.current.destroy();
      drawerChartRef.current = null;
    }

    if (loadingDrawerReport || !drawerChartInfo.hasData) {
      return;
    }

    const el = drawerRef.current;
    if (!el) return;

    const existingChart = Chart.getChart(el);
    if (existingChart) {
      existingChart.destroy();
    }

    const { pts, labels, summary } = drawerChartInfo;
    const up = summary ? summary.up : true;

    const ctx = el.getContext('2d');
    let bgGradient: CanvasGradient | string = up ? 'rgba(33,147,84,0.08)' : 'rgba(217,72,59,0.08)';
    if (ctx) {
      const g = ctx.createLinearGradient(0, 0, 0, 160);
      if (up) {
        g.addColorStop(0, 'rgba(33, 147, 84, 0.22)');
        g.addColorStop(0.8, 'rgba(33, 147, 84, 0.02)');
        g.addColorStop(1, 'rgba(33, 147, 84, 0)');
      } else {
        g.addColorStop(0, 'rgba(217, 72, 59, 0.22)');
        g.addColorStop(0.8, 'rgba(217, 72, 59, 0.02)');
        g.addColorStop(1, 'rgba(217, 72, 59, 0)');
      }
      bgGradient = g;
    }

    drawerChartRef.current = new Chart(el, {
      type: 'line',
      data: {
        labels,
        datasets: [{
          data: pts,
          borderColor: up ? '#219354' : '#D9483B',
          backgroundColor: bgGradient,
          fill: true,
          tension: 0.35,
          pointRadius: pts.length > 50 ? 0 : pts.length > 20 ? 1.5 : 2.5,
          pointHoverRadius: 4.5,
          pointBackgroundColor: up ? '#219354' : '#D9483B',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 1.5,
          borderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#01253C',
            titleColor: '#E2E8F0',
            bodyColor: '#FFFFFF',
            padding: 10,
            cornerRadius: 8,
            displayColors: false,
            callbacks: {
              title: (items: any) => items[0]?.label || '',
              label: (c: any) => {
                const val = Number(c.raw);
                const sign = val < 0 ? '-$' : '$';
                return ` ${t('managerNetwork.drawer.equity', 'Equity')}: ${sign}${Math.abs(val).toLocaleString('en-US')}`;
              }
            }
          }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { maxTicksLimit: 6, font: { size: 10 }, color: '#9AA3AF' }
          },
          y: {
            grace: '5%',
            grid: { color: '#EEF0F3' },
            border: { display: false },
            ticks: {
              font: { size: 10 },
              color: '#9AA3AF',
              callback: (v: any) => {
                const num = Number(v);
                if (Math.abs(num) >= 1000000) return '$' + (num / 1000000).toFixed(1) + 'M';
                if (Math.abs(num) >= 1000) return '$' + (num / 1000).toFixed(0) + 'K';
                return '$' + num.toLocaleString();
              }
            }
          }
        }
      }
    });

    return () => {
      if (drawerChartRef.current) {
        drawerChartRef.current.destroy();
        drawerChartRef.current = null;
      }
    };
  }, [loadingDrawerReport, drawerChartInfo, t]);

  // Compute view values
  const isMobile = w < 768;
  const fullMoney = mw >= 900;
  const up = '#219354', down = '#D9483B', flat = '#6B7280';

  const cnt = (c: number, pv: number) => {
    const d = c - pv, pc = pv ? (d / pv) * 100 : 0;
    return { d, text: `${d >= 0 ? '+' : ''}${d} (${d >= 0 ? '+' : ''}${pc.toFixed(1)}%)` };
  };
  const pctCh = (c: number, pv: number) => {
    const pc = pv ? ((c - pv) / pv) * 100 : 0;
    return { d: c - pv, text: `${pc >= 0 ? '+' : ''}${pc.toFixed(1)}%` };
  };
  const pts = (c: number, pv: number) => {
    const d = c - pv;
    return { d, text: `${d >= 0 ? '+' : ''}${d.toFixed(1)} pts` };
  };

  const kdefs = useMemo(() => {
    const oc = dashboardData?.overview_cards;
    const ch = (m?: { value?: number | string; change_text?: string }) => {
      let text = String(m?.change_text || '').replace(/\s*so với kỳ trước\s*$/i, '').replace(/\s*vs\.? previous period\s*$/i, '');
      const hit = text.match(/^\+(\d+)\s*\(\+100(?:\.0)?%\)$/);
      if (hit && Number(hit[1]) === Number(m?.value) && Number(hit[1]) > 0) {
        text = `+${hit[1]} (${t('managerNetwork.kpi.allNew', 'new')})`;
      }
      return { d: 1, text };
    };
    const noFunded = fundedAccounts === 0;
    if (oc) {
      return [
        ['total', 'users', t('managerNetwork.kpi.totalCms', 'Total Capital Managers'), oc.total_capital_managers.value, ch(oc.total_capital_managers), 'total_cms', t('managerNetwork.kpi.totalCmsTip', 'Capital Managers with an admin-confirmed stage. Unclassified accounts are excluded until reviewed.')],
        ['capital', 'hand-coins', t('managerNetwork.kpi.totalAccountCapital', 'Total Account Capital'), oc.total_funded_capital.formatted || (fullMoney ? money(oc.total_funded_capital.value as number) : compact(oc.total_funded_capital.value as number)), ch(oc.total_funded_capital), 'total_cms', t('managerNetwork.kpi.totalAccountCapitalTip', 'Sum of initial capital across all tracked accounts (Challenge, Verification and Funded).')],
        ['challenge', 'coins', t('managerNetwork.kpi.challenge', 'Đang Challenge'), oc.challenging.value, ch(oc.challenging), 'challenge', t('managerNetwork.kpi.challengeTip', 'CMs that reached Challenge or later (Challenge, Verified, Funded).')],
        ['funded', 'shield-check', t('managerNetwork.kpi.funded', 'Funded'), oc.funded.value, ch(oc.funded), 'funded', t('managerNetwork.kpi.fundedTip', 'CMs whose current confirmed stage is Funded.')],
        ['payout', 'chart-no-axes-column-increasing', t('managerNetwork.kpi.hasPayout', 'Đã có Payout'), oc.has_payout.value, ch(oc.has_payout), 'payout', t('managerNetwork.kpi.hasPayoutTip', 'Funded CMs with at least one verified payout up to the end of the period.')],
        ['vpayout', 'circle-dollar-sign', t('managerNetwork.kpi.totalVerifiedPayout', 'Total Verified Payout'), oc.total_verified_payout.formatted || (fullMoney ? money(oc.total_verified_payout.value as number) : compact(oc.total_verified_payout.value as number)), ch(oc.total_verified_payout), 'payout', t('managerNetwork.kpi.totalVerifiedPayoutTip', 'Total payout amount confirmed by the prop firm (pending payouts excluded).')],
        ['active', 'chart-pie', t('managerNetwork.kpi.activeConsistent', 'Active / Consistent'), noFunded ? '0%' : oc.consistency_rate.value, noFunded ? { d: 0, text: '0.0 pts' } : ch(oc.consistency_rate), 'active_consistent', t('managerNetwork.kpi.activeConsistentTip', 'Share of Funded accounts that traded in the last 7 days and are Normal or Scale Ready. Assumed definition.')]
      ];
    }
    return [
      ['total', 'users', t('managerNetwork.kpi.totalCms', 'Total Capital Managers'), cur.total, cnt(cur.total, prev.total), 'total_cms', t('managerNetwork.kpi.totalCmsTip', 'Capital Managers with an admin-confirmed stage. Unclassified accounts are excluded until reviewed.')],
      ['capital', 'hand-coins', t('managerNetwork.kpi.totalFundedCapital', 'Total Funded Capital'), fullMoney ? money(cur.fundedCapital) : compact(cur.fundedCapital), pctCh(cur.fundedCapital, prev.fundedCapital), 'total_funded_capital', t('managerNetwork.kpi.totalFundedCapitalTip', 'Sum of initial capital across Funded accounts.')],
      ['challenge', 'coins', t('managerNetwork.kpi.challenge', 'Đang Challenge'), cur.reach.Challenge, cnt(cur.reach.Challenge, prev.reach.Challenge), 'challenge', t('managerNetwork.kpi.challengeTip', 'CMs that reached Challenge or later (Challenge, Verified, Funded).')],
      ['funded', 'shield-check', t('managerNetwork.kpi.funded', 'Funded'), cur.reach.Funded, cnt(cur.reach.Funded, prev.reach.Funded), 'funded', t('managerNetwork.kpi.fundedTip', 'CMs whose current confirmed stage is Funded.')],
      ['payout', 'chart-no-axes-column-increasing', t('managerNetwork.kpi.hasPayout', 'Đã có Payout'), cur.reach.Payout, cnt(cur.reach.Payout, prev.reach.Payout), 'payout', t('managerNetwork.kpi.hasPayoutTip', 'Funded CMs with at least one verified payout up to the end of the period.')],
      ['vpayout', 'circle-dollar-sign', t('managerNetwork.kpi.totalVerifiedPayout', 'Total Verified Payout'), fullMoney ? money(cur.verifiedPayout) : compact(cur.verifiedPayout), pctCh(cur.verifiedPayout, prev.verifiedPayout), 'payout', t('managerNetwork.kpi.totalVerifiedPayoutTip', 'Total payout amount confirmed by the prop firm (pending payouts excluded).')],
      ['active', 'chart-pie', t('managerNetwork.kpi.activeConsistent', 'Active / Consistent'), cur.activePct.toFixed(0) + '%', pts(cur.activePct, prev.activePct), 'active_consistent', t('managerNetwork.kpi.activeConsistentTip', 'Share of Funded accounts that traded in the last 7 days and are Normal or Scale Ready. Assumed definition.')]
    ];
  }, [dashboardData, cur, prev, fullMoney, fundedAccounts, t]);

  const tipH = (id: string) => ({
    tipOpen: tip === id,
    onEnter: () => setTip(id),
    onLeave: () => setTip(null)
  });

  const kpis = kdefs.map(([id, icon, label, v, dl, key, tipText]) => {
    const isWide = id === 'capital' || id === 'vpayout';
    const gridCol = mw < 540 ? (isWide || id === 'active' ? 'span 2' : 'span 1') : undefined;
    const isPositive = typeof (dl as any).text === 'string' && (dl as any).text.startsWith('+');
    const isNegative = typeof (dl as any).text === 'string' && (dl as any).text.startsWith('-');
    const color = isPositive ? up : isNegative ? down : flat;
    const arrow = isPositive ? 'arrow-up' : isNegative ? 'arrow-down' : 'minus';
    return {
      id: id as string,
      icon: icon as string,
      label: label as string,
      tip: tipText as string,
      valueText: String(v),
      deltaText: (dl as any).text,
      color,
      arrow,
      onClick: () => {
        const n = typeof v === 'number' ? v : (/^\d+$/.test(String(v)) ? Number(v) : undefined);
        openStatList(id as string, label as string, tipText as string, id === 'capital' || id === 'vpayout' ? undefined : n);
      },
      isWide,
      gridCol,
      ...tipH('k' + id)
    };
  });

  const kpiCols = mw >= 1040
    ? 'minmax(0,1fr) minmax(0,1.45fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1.45fr) minmax(0,1fr)'
    : mw >= 380
      ? 'repeat(2,minmax(0,1fr))'
      : '1fr';

  // Funnel journey
  const jSteps = ['Developing', 'Challenge', 'Verified', 'Funded', 'Payout', 'Consistent'];
  const jLabels: Record<string, string> = {
    Developing: t('managerNetwork.journey.steps.Developing', 'Đang phát triển'),
    Challenge: t('managerNetwork.journey.steps.Challenge', 'Đang Challenge'),
    Verified: t('managerNetwork.journey.steps.Verified', 'Đã xác thực'),
    Funded: t('managerNetwork.journey.steps.Funded', 'Đã cấp vốn (Funded)'),
    Payout: t('managerNetwork.journey.steps.Payout', 'Đã có Payout'),
    Consistent: t('managerNetwork.journey.steps.Consistent', 'Giao dịch nhất quán')
  };
  const jIcons: Record<string, string> = {
    Developing: 'users', Challenge: 'target', Verified: 'badge-check',
    Funded: 'shield-check', Payout: 'hand-coins', Consistent: 'repeat', 'Scale Ready': 'rocket'
  };
  const jFunnel = dashboardData?.journey_funnel || (dashboardData as any)?.journeyFunnel;
  const base = Number(
    jFunnel?.developing?.count ??
    dashboardData?.overview_cards?.total_capital_managers?.value ??
    dashboardData?.network_status?.total_cm ??
    cur.total
  );
  const jNarrow = mw < 760;
  const journey = jSteps.map((k, i) => {
    let c = cur.reach[k] || 0;
    let pct = base ? ((c / base) * 100).toFixed(0) + '%' : '0%';
    let dlText = cnt(c, prev.reach[k] || 0).text;

    if (jFunnel) {
      const stepMap: Record<string, keyof JourneyFunnel> = {
        Developing: 'developing',
        Challenge: 'challenge',
        Verified: 'verification',
        Funded: 'funded',
        Payout: 'payout_achieved',
        Consistent: 'consistent',
      };
      const funnelStep =
        jFunnel[stepMap[k]] ||
        (k === 'Verified' ? (jFunnel as any).verified : undefined) ||
        (k === 'Payout' ? ((jFunnel as any).payout || (jFunnel as any).payoutAchieved) : undefined);
      if (funnelStep) {
        c = Number(funnelStep.count ?? 0);
        const rawPct = funnelStep.percent;
        pct = rawPct !== undefined && rawPct !== null
          ? (String(rawPct).endsWith('%') ? String(rawPct) : `${rawPct}%`)
          : (base ? ((c / base) * 100).toFixed(0) + '%' : '0%');
        dlText = `${c} CMs`;
      }
    }

    const isFirst = i === 0, isLast = i === jSteps.length - 1;
    const clip = jNarrow ? 'none' : isFirst ? 'polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%)' :
      isLast ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 14px 50%)' :
        'polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%, 14px 50%)';
    return {
      step: k, label: jLabels[k] || k, icon: jIcons[k] || 'award', count: c, pct,
      clip, padL: jNarrow ? '12px' : isFirst ? '12px' : '20px', padR: jNarrow ? '12px' : isLast ? '12px' : '20px',
      delta: dlText, dColor: up,
      title: `${jLabels[k] || k}: ${c} CMs (${pct} ${t('managerNetwork.journey.fromTotal', 'of classified network', { count: base })})`,
      onClick: () => {
        const statMap: Record<string, StatDetailType> = {
          Developing: 'developing',
          Challenge: 'challenge',
          Verified: 'verification',
          Funded: 'funded',
          Payout: 'payout_achieved',
          Consistent: 'consistent',
        };
        const statId = statMap[k] || 'developing';
        openStatList(
          statId,
          jLabels[k] || k,
          t(`managerNetwork.journey.tooltips.${k}`, `Capital Managers currently in or beyond ${k} stage.`),
          c
        );
      }
    };
  });

  // Status distribution
  const statusKeys: (keyof typeof STATUS_COLORS)[] = ['Normal', 'Attention', 'Paused', 'Recovery', 'Scale Ready'];
  const net = dashboardData?.network_status as (NetworkStatus & { recovery?: { count: number } }) | undefined;
  const netKey: Record<string, string> = { Normal: 'normal', Attention: 'attention', Paused: 'paused', Recovery: 'recovery', 'Scale Ready': 'scale_ready' };
  const statusCounts = statusKeys.map(k => Number(
    accountStatus ? accountStatus.counts[k] || 0
      : net ? (net as any)[netKey[k]]?.count ?? 0
        : cur.status[k] || 0
  ));
  // Percentages are derived from the counts so the legend and the donut always add up to 100%,
  // even when the API's own percent field is off (e.g. 17 "normal" out of 15 CMs → 113%)
  const statusDenom = accountStatus ? accountStatus.total : Math.max(base, statusCounts.reduce((a, b) => a + b, 0));
  let deg = 0;
  const segs = statusKeys.map((k, i) => {
    const c = statusCounts[i];
    const p = statusDenom ? (c / statusDenom) * 100 : 0;
    const s = deg; deg += p * 3.6;
    return { k, c, p, s, e: deg, color: STATUS_COLORS[k] };
  });
  const donutBg = base ? 'conic-gradient(' + segs.map(s => `${s.color} ${s.s}deg ${s.e}deg`).join(', ') + ')' : '#E6E8EC';
  const statusList = segs.map(stItem => ({
    label: t('managerNetwork.statuses.' + stItem.k, stItem.k),
    count: stItem.c,
    pct: stItem.p.toFixed(0) + '%',
    color: stItem.color,
    onClick: () => openList(
      t('managerNetwork.statuses.listTitle', 'Status: {{status}}', { status: t('managerNetwork.statuses.' + stItem.k, stItem.k) }),
      t('managerNetwork.statuses.listDesc', 'All accounts currently tagged {{status}}.', { status: t('managerNetwork.statuses.' + stItem.k, stItem.k) }),
      'status:' + stItem.k
    )
  }));

  // Capital Under Management & Tiers
  const cum = dashboardData?.capital_under_management || (dashboardData as any)?.capitalUnderManagement;
  const cumTotalFunded = cum?.total_funded_capital ?? (cum as any)?.totalFundedCapital ?? (cum as any)?.total_capital ?? (cum as any)?.totalCapital;
  const cumAccountCount = cum?.account_count ?? (cum as any)?.accountCount ?? (cum as any)?.accounts_count ?? (cum as any)?.accountsCount ?? (cum as any)?.total_accounts;
  const cumAvgSize = cum?.avg_account_size ?? (cum as any)?.avgAccountSize ?? (cum as any)?.average_account_size ?? (cum as any)?.avg_ticket;
  const cumTiers = cum?.tiers ?? (cum as any)?.capital_tiers ?? (cum as any)?.capitalTiers;

  const tierVals = [10000, 25000, 50000, 100000, 200000, 400000];
  const tierKeyMap: Record<number, string> = {
    10000: '10K',
    25000: '25K',
    50000: '50K',
    100000: '100K',
    200000: '200K',
    400000: '400K'
  };

  const getTierCount = (cap: number): number => {
    if (cumTiers) {
      if (Array.isArray(cumTiers)) {
        const k = tierKeyMap[cap]?.toLowerCase();
        const item = cumTiers.find((x: any) =>
          String(x.tier || x.label || x.cap || x.capital || x.key).toLowerCase() === k ||
          Number(x.cap || x.capital || x.value) === cap
        );
        if (item) return Number(item.count ?? item.value ?? 0);
        return 0;
      }
      const k = tierKeyMap[cap];
      const t = cumTiers as Record<string, any>;
      if (t[k] !== undefined) return Number(t[k]);
      if (t[k.toLowerCase()] !== undefined) return Number(t[k.toLowerCase()]);
      if (t[k.toUpperCase()] !== undefined) return Number(t[k.toUpperCase()]);
      if (t['$' + k] !== undefined) return Number(t['$' + k]);
      if (t['$' + k.toLowerCase()] !== undefined) return Number(t['$' + k.toLowerCase()]);
      if (t[String(cap)] !== undefined) return Number(t[String(cap)]);
      if (t[cap] !== undefined) return Number(t[cap]);
      return 0;
    }
    return filteredSet.filter(a => a.stage === 'Funded' && a.initialCapital === cap).length;
  };

  const maxTier = Math.max(1, ...tierVals.map(cap => getTierCount(cap)));

  const tiers = tierVals.map(cap => {
    const c = getTierCount(cap);
    const formattedCap = '$' + cap.toLocaleString(i18n.language === 'vi' ? 'vi-VN' : 'en-US');
    return {
      cap,
      label: '$' + (cap >= 1000 ? (cap / 1000) + 'K' : cap),
      count: c,
      h: Math.max(4, Math.round((c / maxTier) * 100)) + 'px',
      title: t('managerNetwork.cum.tierTitle', '{{count}} Funded accounts at {{cap}}', { count: c, cap: formattedCap }),
      onClick: () => onSelectTierCapital(cap),
      isSelected: selectedTierCapital === cap
    };
  });

  // Key indicators
  const lvl = (v: number, g: number, wv: number, higherBetter = true) => higherBetter ? (v >= g ? 0 : v >= wv ? 1 : 2) : 0;
  const LV = [
    [t('managerNetwork.keyInd.status.Good', 'Good'), '#219354'],
    [t('managerNetwork.keyInd.status.Watch', 'Watch'), '#E09C2F'],
    [t('managerNetwork.keyInd.status.Risk', 'Risk'), '#D9483B']
  ];

  const keyInd = useMemo(() => {
    if (dashboardData?.key_indicators) {
      const ki = dashboardData.key_indicators;
      const getDelta = (trendStr: string) => {
        const isPos = trendStr.startsWith('+');
        const isNeg = trendStr.startsWith('-');
        return {
          text: trendStr,
          color: isPos ? up : isNeg ? down : flat,
          arrow: isPos ? 'arrow-up' : isNeg ? 'arrow-down' : 'minus'
        };
      };

      const vpD = getDelta(ki.verified_payout.trend);
      const afaCapped = fundedAccounts !== null && Number(ki.active_funded_acc.value) > fundedAccounts;
      const afaD = getDelta(afaCapped ? '+0' : ki.active_funded_acc.trend);
      const csD = getDelta(ki.consistency_score.trend);
      const adD = getDelta(ki.average_drawdown.trend);
      const scD = getDelta(ki.system_compliance.trend);
      // Average of our computed risk scores (accounts without trades are left out)
      const riskScores = Object.values(riskByAccount).filter((r): r is RiskCalc => Boolean(r)).map(r => r.score);
      const riskAvg = riskScores.length ? Math.round(riskScores.reduce((a, b) => a + b, 0) / riskScores.length) : null;

      const items = [
        {
          label: t('managerNetwork.keyInd.labels.verifiedPayout', 'Verified Payout'),
          valueText: String(ki.verified_payout.value),
          prevText: '$0',
          tip: t('managerNetwork.keyInd.tips.verifiedPayout', 'Sum of verified payouts.'),
          deltaText: vpD.text,
          deltaColor: vpD.color,
          arrow: vpD.arrow,
          statusLabel: t('managerNetwork.keyInd.status.Good', 'Good'),
          statusColor: '#219354'
        },
        {
          label: t('managerNetwork.keyInd.labels.activeFundedAcc', 'Active Funded Accounts'),
          valueText: String(afaCapped ? fundedAccounts : ki.active_funded_acc.value),
          prevText: '1',
          tip: t('managerNetwork.keyInd.tips.activeFundedAcc', 'Funded accounts not paused and with a trade in the last 7 days.'),
          deltaText: afaD.text,
          deltaColor: afaD.color,
          arrow: afaD.arrow,
          statusLabel: t('managerNetwork.keyInd.status.Good', 'Good'),
          statusColor: '#219354'
        },
        {
          label: t('managerNetwork.keyInd.labels.consistencyScore', 'Consistency Score'),
          valueText: String(ki.consistency_score.value),
          prevText: '70',
          tip: t('managerNetwork.keyInd.tips.consistencyScoreAll', 'Average consistency score (0–100) across tracked accounts. Target ≥ 75.'),
          deltaText: csD.text,
          deltaColor: csD.color,
          arrow: csD.arrow,
          statusLabel: Number(ki.consistency_score.value) >= 75 ? t('managerNetwork.keyInd.status.Good', 'Good') : t('managerNetwork.keyInd.status.Watch', 'Watch'),
          statusColor: Number(ki.consistency_score.value) >= 75 ? '#219354' : '#E09C2F'
        },
        {
          label: t('managerNetwork.keyInd.labels.averageDrawdown', 'Average Drawdown'),
          valueText: String(ki.average_drawdown.value),
          prevText: '0.0%',
          tip: t('managerNetwork.keyInd.tips.averageDrawdownAll', 'Average current drawdown across tracked accounts.'),
          deltaText: adD.text,
          deltaColor: adD.color,
          arrow: adD.arrow,
          statusLabel: t('managerNetwork.keyInd.status.Good', 'Good'),
          statusColor: '#219354'
        },
        {
          label: t('managerNetwork.keyInd.labels.systemCompliance', 'System Compliance'),
          valueText: String(ki.system_compliance.value),
          prevText: '85%',
          tip: t('managerNetwork.keyInd.tips.systemCompliance', 'Average adherence to LUMIR trading system rules.'),
          deltaText: scD.text,
          deltaColor: scD.color,
          arrow: scD.arrow,
          statusLabel: parseInt(String(ki.system_compliance.value)) >= 90 ? t('managerNetwork.keyInd.status.Good', 'Good') : t('managerNetwork.keyInd.status.Watch', 'Watch'),
          statusColor: parseInt(String(ki.system_compliance.value)) >= 90 ? '#219354' : '#E09C2F'
        },
        {
          label: t('managerNetwork.keyInd.labels.riskCompliance', 'Risk Compliance'),
          valueText: riskAvg !== null ? riskAvg + '%' : '…',
          prevText: '85%',
          tip: t('managerNetwork.keyInd.tips.riskComplianceCalc', 'Average risk score of accounts with trades: 40% max drawdown vs 10%, 30% worst day vs 5%, 30% losing trades above 1% of capital.'),
          // No previous-period value for our own score, so no trend
          deltaText: '',
          deltaColor: flat,
          arrow: 'minus',
          statusLabel: riskAvg === null ? '' : riskAvg >= 80 ? t('managerNetwork.keyInd.status.Good', 'Good') : riskAvg >= 60 ? t('managerNetwork.keyInd.status.Watch', 'Watch') : t('managerNetwork.keyInd.status.Risk', 'Risk'),
          statusColor: riskAvg === null ? flat : riskAvg >= 80 ? '#219354' : riskAvg >= 60 ? '#E09C2F' : '#D9483B'
        }
      ];

      return items.map((item, i) => ({
        ...item,
        ...tipH('ki' + i)
      }));
    }

    const kiDefs: [string, string, string, { d: number; text: string }, number, string, string][] = [
      [t('managerNetwork.keyInd.labels.verifiedPayout', 'Verified Payout'), compact(cur.verifiedPayout).replace('.0K', 'K'), compact(prev.verifiedPayout), pctCh(cur.verifiedPayout, prev.verifiedPayout), cur.verifiedPayout >= prev.verifiedPayout ? 0 : 1, 'kpi:vpayout', t('managerNetwork.keyInd.tips.verifiedPayout', 'Sum of verified payouts (business data from payout records).')],
      [t('managerNetwork.keyInd.labels.activeFundedAcc', 'Active Funded Accounts'), String(cur.activeFunded), String(prev.activeFunded), cnt(cur.activeFunded, prev.activeFunded), 0, 'kpi:active', t('managerNetwork.keyInd.tips.activeFundedAcc', 'Funded accounts not paused and with a trade in the last 7 days (bot/API activity).')],
      [t('managerNetwork.keyInd.labels.consistencyScore', 'Consistency Score'), cur.consistency.toFixed(0), prev.consistency.toFixed(0), pts(cur.consistency, prev.consistency), lvl(cur.consistency, 75, 60), 'kpi:funded', t('managerNetwork.keyInd.tips.consistencyScore', 'Average consistency score (0–100) across Funded accounts. Target ≥ 75.')],
      [t('managerNetwork.keyInd.labels.averageDrawdown', 'Average Drawdown'), cur.avgDD.toFixed(1) + '%', prev.avgDD.toFixed(1) + '%', pts(cur.avgDD, prev.avgDD), cur.avgDD > -5 ? 0 : cur.avgDD > -8 ? 1 : 2, 'kpi:funded', t('managerNetwork.keyInd.tips.averageDrawdown', 'Average current drawdown across Funded accounts. Watch below −5%, risk below −8%.')],
      [t('managerNetwork.keyInd.labels.systemCompliance', 'System Compliance'), cur.sysComp.toFixed(0) + '%', prev.sysComp.toFixed(0) + '%', pts(cur.sysComp, prev.sysComp), lvl(cur.sysComp, 95, 90), 'kpi:cls', t('managerNetwork.keyInd.tips.systemCompliance', 'Average adherence to LUMIR trading system rules. Target ≥ 95%.')],
      [t('managerNetwork.keyInd.labels.riskCompliance', 'Risk Compliance'), cur.riskComp.toFixed(0) + '%', prev.riskComp.toFixed(0) + '%', pts(cur.riskComp, prev.riskComp), lvl(cur.riskComp, 95, 90), 'kpi:cls', t('managerNetwork.keyInd.tips.riskCompliance', 'Average adherence to risk limits (lot size, daily loss, max DD). Target ≥ 95%.')]
    ];
    return kiDefs.map(([label, valueText, prevText, dl, lvIdx, key, tipText], i) => ({
      label, valueText, prevText, tip: tipText, deltaText: dl.text, deltaColor: dl.d > 0 ? up : dl.d < 0 ? down : flat,
      arrow: dl.d > 0 ? 'arrow-up' : dl.d < 0 ? 'arrow-down' : 'minus',
      statusLabel: LV[lvIdx][0], statusColor: LV[lvIdx][1],
      ...tipH('ki' + i)
    }));
  }, [dashboardData, cur, prev, fundedAccounts, riskByAccount, t]);

  // Top Capital Managers table (v2)
  const scoreOf = (a: AccountData) => Math.round(0.3 * a.systemCompliance + 0.3 * a.riskCompliance + 0.4 * a.consistencyScore);
  const improveOf = (_a: AccountData) => 0;
  const stageRank: Record<string, number> = { Demo: 0, Challenge: 1, Verified: 2, Funded: 3 };
  const tv: Record<string, (a: AccountData) => any> = {
    cmId: a => a.accountId,
    stage: a => stageRank[a.stage] ?? -1,
    capital: a => a.initialCapital,
    payout: a => a.payouts.filter(x => x.verified).length,
    discipline: a => a.systemCompliance,
    risk: a => a.riskCompliance,
    consistency: a => a.consistencyScore,
    score: scoreOf,
    trend: improveOf
  };
  const tdir = topSort.dir === 'asc' ? 1 : -1;
  const tget = tv[topSort.key] || tv.score;

  const tableAll = useMemo(() => {
    return filteredSet
      .filter(a => a.stage !== 'Unclassified' && matchesSearch(a, search))
      .sort((a, b) => {
        const x = tget(a), y = tget(b);
        return (typeof x === 'string' ? x.localeCompare(y) : x - y) * tdir || scoreOf(b) - scoreOf(a);
      });
  }, [filteredSet, search, tget, tdir, matchesSearch]);

  // Per-row breakdown shown when hovering a Risk value
  const riskTipOf = useCallback((calc: RiskCalc | null | undefined, risk: number | null | undefined) => {
    if (risk === undefined) return t('managerNetwork.top.tips.riskLoading', 'Loading trade report…');
    if (!calc) return t('managerNetwork.top.tips.riskNoTrades', 'No closed trades yet, so no risk score.');
    return t('managerNetwork.top.tips.riskRow', 'Max DD {{dd}}% / 10% · worst day {{day}}% / 5% · {{over}}/{{losses}} losing trades above 1%', {
      dd: calc.maxDrawdownPct.toFixed(1),
      day: calc.worstDayPct.toFixed(1),
      over: calc.oversizedLosses,
      losses: calc.losingTrades
    });
  }, [t]);

  // How each column of the ranking table is calculated (header tooltips)
  const topColTips: Record<string, string> = {
    rank: t('managerNetwork.top.tips.rank', 'Rank by LUMIR Score, highest first, across every CM in the list (not only the rows shown).'),
    cmId: t('managerNetwork.top.tips.cmId', 'Trading account number.'),
    stage: t('managerNetwork.top.tips.stage', 'Current stage of the account in the system: Challenge → Verification → Funded.'),
    capital: t('managerNetwork.top.tips.capital', 'Initial capital (account size) of the account.'),
    payout: t('managerNetwork.top.tips.payout', '✓ when the account has at least one payout verified by the prop firm; -- when it has none.'),
    discipline: t('managerNetwork.top.tips.discipline', 'Compliance with the LUMIR trading system (0–100). Provided by the LUMIR system (API).'),
    risk: t('managerNetwork.top.tips.risk', 'Calculated from closed trades (0–100): 40% max drawdown vs the 10% limit + 30% worst day vs the 5% daily limit + 30% share of losing trades above 1% of capital. "—" = no trades yet. Hover a value for its breakdown.'),
    consistency: t('managerNetwork.top.tips.consistency', '(1 − |result of the biggest day| ÷ total |result| of all trading days) × 100. Higher = profit spread over many days instead of one big day.'),
    score: t('managerNetwork.top.tips.score', '(Discipline + Risk + Consistency) ÷ 3. Without a Risk score: (Discipline + Consistency) ÷ 2.'),
    trend: t('managerNetwork.top.tips.trend', '▲ improving / ▼ declining compared with the previous period, as reported by the LUMIR system (API).')
  };

  const topCols = [
    ['cmId', t('managerNetwork.top.cols.cmId', 'CM ID')],
    ['stage', t('managerNetwork.top.cols.stage', 'Level')],
    ['capital', t('managerNetwork.top.cols.capital', 'Capital')],
    ['payout', t('managerNetwork.top.cols.payout', 'Payout')],
    ['discipline', t('managerNetwork.top.cols.discipline', 'Discipline')],
    ['risk', t('managerNetwork.top.cols.risk', 'Risk')],
    ['consistency', t('managerNetwork.top.cols.consistency', 'Consistency')],
    ['score', t('managerNetwork.top.cols.score', 'LUMIR Score')],
    ['trend', t('managerNetwork.top.cols.trend', 'Trend')]
  ].map(([id, label]) => {
    const on = topSort.key === id;
    return {
      key: id, label, tip: topColTips[id], color: on ? '#8C6424' : '#3A342C', weight: on ? 700 : 400,
      icon: on ? (topSort.dir === 'asc' ? 'arrow-up' : 'arrow-down') : 'chevrons-up-down',
      onClick: () => {
        setTopSort(cu => ({ key: id, dir: cu.key === id ? (cu.dir === 'desc' ? 'asc' : 'desc') : (id === 'cmId' ? 'asc' : 'desc') }));
        setExpanded(false);
      }
    };
  });

  const rowOf = useCallback((a: AccountData) => {
    const st = statusOf(a);
    const [sc, sb] = STAGE_STYLE[a.stage] || ['#2563EB', '#EFF6FF'];
    const cmName = a.cmName || a.accountId || 'CM';
    return {
      accountId: a.accountId,
      cmName: cmName,
      alias: a.alias,
      initial: (a.cmName && a.cmName[0]) || (a.accountId && a.accountId[0]) || 'C',
      platform: a.platform,
      platformTag: (a.platform || '').toUpperCase(),
      stage: a.stage,
      stageColor: sc,
      stageBg: sb,
      capital: money(a.initialCapital),
      balance: money(a.balance, true),
      pnl: (a.pnl >= 0 ? '+' : '') + money(a.pnl),
      pnlColor: a.pnl >= 0 ? up : down,
      dd: a.drawdown.toFixed(1) + '%',
      ddColor: a.drawdown <= -8 ? down : a.drawdown <= -5 ? '#C27C12' : '#1B2430',
      days: a.tradingDays,
      status: st,
      statusColor: STATUS_COLORS[st],
      onClick: () => {
        setDrawerId(a.accountId);
        setDrawerAccountNumber(a.login);
        setDrawerAccountInfo({
          cmName: a.cmName,
          alias: a.alias,
          login: a.login,
          platform: a.platform,
          stage: a.stage,
          capital: a.initialCapital,
          balance: a.balance,
          drawdown: a.drawdown,
          discipline: a.systemCompliance,
          risk: a.riskCompliance,
          consistency: a.consistencyScore,
          score: scoreOf(a)
        });
      }
    };
  }, []);

  const limit = 5;
  const medals: [string, string, string][] = [
    ['#F3D98B', '#C9A13E', '#6B4E0E'],
    ['#E4E7EB', '#A9AFB8', '#3A3F47'],
    ['#EBC3A0', '#B97A45', '#5A3312']
  ];

  const rows = useMemo(() => {
    if (topManagersApiData && topManagersApiData.length > 0) {
      // Our risk score replaces the API's; the LUMIR score and the ranking are recomputed from it
      const riskOf = (item: TopManagerItem): number | null | undefined => {
        const acc = String(item.account_number || '');
        if (!(acc in riskByAccount)) return undefined; // still loading
        return riskByAccount[acc] ? riskByAccount[acc]!.score : null;
      };
      const scoreOf = (item: TopManagerItem) => {
        const r = riskOf(item);
        return r === undefined ? null : lumirScore(item.discipline ?? 0, r, item.consistency ?? 0);
      };
      const rankOf: Record<string, number> = {};
      [...topManagersApiData]
        .sort((a, b) => ((scoreOf(b) ?? -1) - (scoreOf(a) ?? -1)) || ((a.rank ?? 0) - (b.rank ?? 0)))
        .forEach((item, idx) => { rankOf[String(item.account_number)] = idx + 1; });

      const list = [...topManagersApiData];
      const dir = topSort.dir === 'asc' ? 1 : -1;
      list.sort((a, b) => {
        let va: any = (a as any)[topSort.key];
        let vb: any = (b as any)[topSort.key];
        if (topSort.key === 'score') { va = scoreOf(a) ?? -1; vb = scoreOf(b) ?? -1; }
        else if (topSort.key === 'risk') { va = riskOf(a) ?? -1; vb = riskOf(b) ?? -1; }
        else if (topSort.key === 'cmId') { va = a.account_number || ''; vb = b.account_number || ''; }
        else if (topSort.key === 'stage') { va = a.level || ''; vb = b.level || ''; }
        else if (topSort.key === 'capital') { va = a.capital || 0; vb = b.capital || 0; }
        else if (topSort.key === 'payout') { va = a.payout ? 1 : 0; vb = b.payout ? 1 : 0; }
        else if (topSort.key === 'trend') {
          const orderMap: Record<string, number> = { up: 2, stable: 1, down: 0 };
          va = orderMap[a.trend] ?? 1;
          vb = orderMap[b.trend] ?? 1;
        }
        if (va === undefined || va === null) va = typeof vb === 'string' ? '' : (a.lumir_score ?? 0);
        if (vb === undefined || vb === null) vb = typeof va === 'string' ? '' : (b.lumir_score ?? 0);
        return (typeof va === 'string' ? va.localeCompare(String(vb)) : (Number(va) - Number(vb))) * dir;
      });

      const displayList = list.slice(0, topVisible);
      return displayList.map((item, i) => {
        const rank = rankOf[String(item.account_number)] || item.rank || (i + 1);
        const md = medals[rank - 1] || ['#FFFFFF', '#CFC4B2', '#1B1B1B'];
        const risk = riskOf(item);
        const score = scoreOf(item);
        const paid = Boolean(item.payout);
        const [sc, sb] = STAGE_STYLE[item.level as StageType] || ['#2563EB', '#EFF6FF'];
        const cmIdStr = item.account_number || (item as any)?.name || (item.id ? `CM #${item.id}` : 'CM');
        const initial = cmIdStr ? (cmIdStr[0] || 'C').toUpperCase() : 'C';
        const level = item.level || 'CM-I';
        const capitalFormatted = item.capital_formatted || (item.capital ? `$${Number(item.capital).toLocaleString()}` : '$0');
        const platform = (item.platform || 'MT5').toUpperCase() === 'CTRADER' ? 'cTrader' : (item.platform || 'MT5').toUpperCase();

        return {
          accountId: cmIdStr,
          cmName: cmIdStr,
          alias: level,
          initial: initial,
          platform,
          platformTag: platform,
          stage: level,
          stageColor: sc,
          stageBg: sb,
          capital: capitalFormatted,
          balance: capitalFormatted,
          pnl: '+$0',
          pnlColor: '#219354',
          dd: '0.0%',
          ddColor: '#1B2430',
          days: 30,
          status: 'Normal',
          statusColor: '#219354',
          onClick: () => {
            const cleanCmId = cmIdStr.replace('CM ', '');
            const match = accounts.find(x => (x.accountId && x.accountId.includes(cleanCmId)) || (x.cmName && x.cmName.includes(cmIdStr)));
            const accNum = match?.login || cmIdStr.replace(/\D/g, '') || '17217317';
            if (match) {
              setDrawerId(match.accountId);
              setDrawerAccountNumber(match.login);
            } else if (accounts[0]) {
              setDrawerId(accounts[0].accountId);
              setDrawerAccountNumber(accNum);
            } else {
              setDrawerId(cmIdStr);
              setDrawerAccountNumber(accNum);
            }
            setDrawerAccountInfo({
              cmName: cmIdStr,
              alias: level,
              login: accNum,
              platform,
              stage: level,
              capital: item.capital || 0,
              capitalFormatted: capitalFormatted,
              discipline: item.discipline ?? 0,
              risk: typeof risk === 'number' ? risk : null,
              consistency: item.consistency ?? 0,
              score: score ?? item.lumir_score ?? 0
            });
          },
          rank,
          medalBg: md[0],
          medalBorder: md[1],
          medalColor: md[2],
          cmId: cmIdStr,
          level: level,
          payoutMark: paid ? '✓' : '--',
          payoutColor: paid ? '#3FA34D' : '#9A9187',
          payoutSize: paid ? '15px' : '12px',
          discipline: item.discipline ?? 0,
          // "…" while the trade report loads, "—" when the account has no closed trades yet
          risk: risk === undefined ? '…' : risk === null ? '—' : risk,
          consistency: item.consistency ?? 0,
          score: score === null ? '…' : score,
          riskTip: riskTipOf(riskByAccount[String(item.account_number)], risk),
          scoreTip: score === null ? '' : (typeof risk === 'number'
            ? `(${item.discipline ?? 0} + ${risk} + ${item.consistency ?? 0}) ÷ 3 = ${score}`
            : `(${item.discipline ?? 0} + ${item.consistency ?? 0}) ÷ 2 = ${score}`),
          trendMark: item.trend === 'up' ? '▲' : item.trend === 'down' ? '▼' : '••',
          trendColor: item.trend === 'up' ? '#3FA34D' : item.trend === 'down' ? '#E0463C' : '#8A8278',
          trendSize: item.trend === 'up' || item.trend === 'down' ? '10px' : '12px',
          trendTitle: `Trend: ${item.trend || 'stable'}`
        };
      });
    }

    return [];
  }, [topManagersApiData, topSort, accounts, riskByAccount, topVisible, riskTipOf]);

  // Drawer selected account
  const da = accounts.find(x => x.accountId === drawerId) ||
    accounts.find(x => x.login === drawerAccountNumber);
  const d = useMemo(() => {
    if (!drawerId) return null;
    const st = da ? statusOf(da) : 'Normal';
    const on = da ? !da.paused : true;
    const rc = (v: number) => v >= 80 ? down : v >= 50 ? '#E09C2F' : up;
    const chk = (label: string, ok: boolean, value: string) => ({ label, value, icon: ok ? 'circle-check' : 'circle-x', color: ok ? up : down });

    const rep = drawerReport?.data?.data || drawerReport?.data || drawerReport;
    const accInfo = rep?.account_info;

    const cmName = drawerAccountInfo?.cmName || (da ? da.cmName : drawerId);
    const alias = drawerAccountInfo?.alias || (da ? da.alias : 'Funded');
    const login = accInfo?.account_number
      ? String(accInfo.account_number)
      : (drawerAccountInfo?.login || drawerAccountNumber || (da ? da.login : drawerId));

    const platformTag = accInfo?.platform || drawerAccountInfo?.platform || (da ? da.platform : 'MT5');
    const stage = (
      drawerAccountInfo?.stage ||
      (rep?.ftmo_progress?.account_type && /funded|ftmo account/i.test(rep.ftmo_progress.account_type) ? 'Funded' :
        rep?.ftmo_progress?.account_type && /verification/i.test(rep.ftmo_progress.account_type) ? 'Verified' :
          rep?.ftmo_progress?.account_type && /challenge/i.test(rep.ftmo_progress.account_type) ? 'Challenge' :
            da?.stage) ||
      'Funded'
    ) as StageType;
    const [sc, sb] = STAGE_STYLE[stage] || (da ? STAGE_STYLE[da.stage] : null) || ['#1F7A6E', '#DDF1EE'];

    const hasRep = Boolean(
      rep && (
        accInfo ||
        rep.performance_summary ||
        rep.today_balance !== undefined ||
        rep.balance !== undefined ||
        rep.current_equity !== undefined ||
        rep.sum_net_profit !== undefined ||
        rep.total_trades !== undefined ||
        rep.count_trading_days !== undefined ||
        rep.risk_management ||
        rep.ftmo_progress ||
        (Array.isArray(rep.timeseries_by_trades) && rep.timeseries_by_trades.length > 0) ||
        (Array.isArray(rep.daily_summary) && rep.daily_summary.length > 0)
      )
    );

    // Initial Capital (only from API)
    const initialCapital = accInfo?.initial_balance !== undefined
      ? Number(accInfo.initial_balance)
      : (rep?.initial_balance !== undefined
        ? Number(rep.initial_balance)
        : (drawerAccountInfo?.capital !== undefined ? Number(drawerAccountInfo.capital) : null));

    // Balance (only from API)
    const balance = accInfo?.balance !== undefined
      ? Number(accInfo.balance)
      : (rep?.today_balance !== undefined
        ? Number(rep.today_balance)
        : (rep?.balance !== undefined
          ? Number(rep.balance)
          : (rep?.performance_summary?.current_balance !== undefined
            ? Number(rep.performance_summary.current_balance)
            : (drawerAccountInfo?.capital !== undefined ? Number(drawerAccountInfo.capital) : null))));

    // Equity (only from API)
    const equity = rep?.current_equity !== undefined
      ? Number(rep.current_equity)
      : (rep?.performance_summary?.current_equity !== undefined
        ? Number(rep.performance_summary.current_equity)
        : balance);

    // PnL (only from API)
    const pnl = balance !== null && initialCapital !== null && hasRep && !isNaN(balance) && !isNaN(initialCapital)
      ? balance - initialCapital
      : (rep?.sum_net_profit !== undefined
        ? Number(rep.sum_net_profit)
        : (rep?.performance_summary?.net_profit !== undefined ? Number(rep.performance_summary.net_profit) : null));

    // Drawdown (only from API / realistic calculations)
    let drawdown: number | null = null;
    if (rep?.ftmo_progress?.max_loss?.current_loss !== undefined) {
      const curLoss = Math.abs(Number(rep.ftmo_progress.max_loss.current_loss));
      const maxLossLimit = Number(rep.ftmo_progress.max_loss.max_loss);
      if (maxLossLimit > 0) {
        drawdown = -(curLoss / maxLossLimit) * 10;
      } else if (initialCapital && initialCapital > 0) {
        drawdown = -(curLoss / initialCapital) * 100;
      }
    } else if (rep?.risk_management?.max_drawdown_percent !== undefined) {
      const rawDd = Math.abs(Number(rep.risk_management.max_drawdown_percent));
      if (rawDd > 100) {
        if (initialCapital && initialCapital > 0 && (rawDd / initialCapital) * 100 <= 100) {
          drawdown = -((rawDd / initialCapital) * 100);
        } else {
          drawdown = -(rawDd / 100);
        }
      } else {
        drawdown = -rawDd;
      }
    } else if (rep?.risk_management?.max_drawdown !== undefined && initialCapital && initialCapital > 0) {
      drawdown = -(Math.abs(Number(rep.risk_management.max_drawdown)) / initialCapital) * 100;
    } else if (rep?.max_loss !== undefined && initialCapital && initialCapital > 0) {
      drawdown = -(Math.abs(Number(rep.max_loss)) / initialCapital) * 100;
    } else if (drawerAccountInfo?.drawdown !== undefined) {
      drawdown = Number(drawerAccountInfo.drawdown);
    } else if (da?.drawdown !== undefined && !isNaN(da.drawdown)) {
      drawdown = da.drawdown;
    } else if (initialCapital && initialCapital > 0 && (balance !== null || equity !== null)) {
      const curVal = equity !== null ? equity : balance!;
      const diff = curVal - initialCapital;
      drawdown = diff < 0 ? -((Math.abs(diff) / initialCapital) * 100) : 0;
    }

    if (drawdown !== null) {
      if (drawdown < -100) drawdown = drawdown / 100;
      if (drawdown > 0) drawdown = -drawdown;
    }

    // Trading Days & Trades (only from API)
    const tradingDays = rep?.count_trading_days !== undefined
      ? Number(rep.count_trading_days)
      : null;

    const trades = rep?.total_trades !== undefined
      ? Number(rep.total_trades)
      : (rep?.performance_summary?.total_trades !== undefined
        ? Number(rep.performance_summary.total_trades)
        : null);

    // 1. Dữ liệu giao dịch (Trading Data Tiles)
    const tiles: DrawerTileItem[] = [];
    if (hasRep || drawerAccountInfo?.capital !== undefined) {
      if (initialCapital !== null && !isNaN(initialCapital)) {
        tiles.push({ label: t('managerNetwork.drawer.tiles.initialCapital', 'Initial Capital'), value: money(initialCapital, true), bg: '#FFF7EA', color: '#0F1B2D' });
      }
      if (balance !== null && !isNaN(balance)) {
        tiles.push({ label: t('managerNetwork.drawer.tiles.balance', 'Balance'), value: money(balance, true), bg: '#EAFAF6', color: '#1F7A6E' });
      }
      if (equity !== null && !isNaN(equity)) {
        tiles.push({ label: t('managerNetwork.drawer.tiles.equity', 'Equity'), value: money(equity, true), bg: '#EAFAF6', color: '#1F7A6E' });
      }
      if (pnl !== null && !isNaN(pnl)) {
        tiles.push({ label: t('managerNetwork.drawer.tiles.pnl', 'P&L'), value: (pnl >= 0 ? '+' : '') + money(pnl, true), bg: '#FFF7EA', color: pnl >= 0 ? up : down });
      }
      if (drawdown !== null && !isNaN(drawdown)) {
        const absDd = Math.abs(drawdown);
        const ddDisplay = absDd < 0.05 ? '0.0%' : `${drawdown.toFixed(1)}%`;
        tiles.push({ label: t('managerNetwork.drawer.tiles.drawdown', 'Drawdown'), value: ddDisplay, bg: '#FFF7EA', color: drawdown <= -5 ? down : '#0F1B2D' });
      }
      if ((tradingDays !== null && !isNaN(tradingDays)) || (trades !== null && !isNaN(trades))) {
        const daysPart = tradingDays !== null ? String(tradingDays) : '—';
        const tradesPart = trades !== null ? `${trades} ${t('managerNetwork.drawer.tiles.tradesUnit', 'trades')}` : '';
        const combined = tradesPart ? `${daysPart} · ${tradesPart}` : daysPart;
        tiles.push({ label: t('managerNetwork.drawer.tiles.tradingDays', 'Trading Days'), value: combined, bg: '#FFF7EA', color: '#0F1B2D' });
      }
    }
    const hasTradingData = tiles.length > 0;

    // 2. Chỉ số rủi ro (Risk Indicators)
    const risk: DrawerRiskItem[] = [];
    if (drawdown !== null && !isNaN(drawdown)) {
      const absDd = Math.abs(drawdown);
      const ddPct = Math.min(100, Math.max(0, (absDd / 10) * 100));
      const headroom = (10 + drawdown) > 0 ? (10 + drawdown).toFixed(1) : '0.0';
      const ddDisplay = absDd < 0.05 ? '0.0%' : `${drawdown.toFixed(1)}%`;
      risk.push({
        label: t('managerNetwork.drawer.risk.drawdownVsLimit', 'Drawdown vs max limit (−10%)'),
        value: ddDisplay,
        w: ddPct + '%',
        color: rc(ddPct),
        note: t('managerNetwork.drawer.risk.headroomBeforeBreach', '{{headroom}}% headroom before breach', { headroom })
      });
    }

    const dailyLimit = Number(
      rep?.ftmo_progress?.daily_loss?.max_loss ||
      rep?.today_permitted_loss ||
      (initialCapital && initialCapital > 0 ? initialCapital * 0.05 : 0)
    );

    const dailyLossAmount = rep?.ftmo_progress?.daily_loss?.current_loss !== undefined
      ? Math.abs(Number(rep.ftmo_progress.daily_loss.current_loss))
      : (rep?.max_daily_loss !== undefined
        ? Math.abs(Number(rep.max_daily_loss))
        : (rep?.net_profit_today !== undefined && Number(rep.net_profit_today) < 0
          ? Math.abs(Number(rep.net_profit_today))
          : null));

    const dailyLossUsed = dailyLimit > 0 && dailyLossAmount !== null && !isNaN(dailyLossAmount)
      ? Math.round((dailyLossAmount / dailyLimit) * 100)
      : null;

    if (dailyLossUsed !== null && !isNaN(dailyLossUsed)) {
      const dailyLossDisplay = Math.max(0, dailyLossUsed);
      risk.push({
        label: t('managerNetwork.drawer.risk.dailyLossUsed', 'Daily loss limit used'),
        value: dailyLossDisplay + '%',
        w: Math.min(100, dailyLossDisplay) + '%',
        color: rc(dailyLossDisplay),
        note: t('managerNetwork.drawer.risk.worstDayInPeriod', 'Worst day in current period')
      });
    }

    // Same calculation as the ranking table, from this account's trades
    const riskCalc = riskFromReport(rep);
    const riskScore = riskCalc ? riskCalc.score : null;

    if (riskScore !== null && riskCalc) {
      risk.push({
        label: t('managerNetwork.drawer.risk.riskComplianceScore', 'Risk compliance score'),
        value: riskScore + '%',
        w: Math.min(100, riskScore) + '%',
        color: riskScore >= 80 ? up : riskScore >= 60 ? '#E09C2F' : down,
        note: t('managerNetwork.drawer.risk.riskBreakdown', 'Max DD {{dd}}% · worst day {{day}}% · {{over}}/{{losses}} losing trades above 1%', {
          dd: riskCalc.maxDrawdownPct.toFixed(1),
          day: riskCalc.worstDayPct.toFixed(1),
          over: riskCalc.oversizedLosses,
          losses: riskCalc.losingTrades
        })
      });
    }

    const systemScore = drawerAccountInfo?.discipline !== undefined
      ? Number(drawerAccountInfo.discipline)
      : (rep?.system_score !== undefined ? Number(rep.system_score) : null);

    if (systemScore !== null && !isNaN(systemScore)) {
      risk.push({
        label: t('managerNetwork.drawer.risk.systemComplianceScore', 'System compliance score'),
        value: systemScore + '%',
        w: Math.min(100, systemScore) + '%',
        color: systemScore >= 90 ? up : '#E09C2F',
        note: t('managerNetwork.drawer.risk.targetScore', 'Target ≥ 95%')
      });
    }
    const hasRiskData = risk.length > 0;

    // 3. Chỉ số tuân thủ (Compliance Indicators)
    const comp: DrawerCompItem[] = [];
    if (drawdown !== null && !isNaN(drawdown)) {
      const isDrawdownBreached = rep?.ftmo_progress?.max_loss?.is_breached === true;
      const isDrawdownOk = isDrawdownBreached ? false : drawdown > -10;
      const absDd = Math.abs(drawdown);
      const ddDisplay = absDd < 0.05 ? '0.0%' : `${drawdown.toFixed(1)}%`;
      comp.push(chk(
        t('managerNetwork.drawer.comp.drawdownWithinLimit', 'Drawdown within limit'),
        isDrawdownOk,
        ddDisplay + ' / −10%'
      ));
    }

    if (dailyLossUsed !== null && !isNaN(dailyLossUsed)) {
      comp.push(chk(
        t('managerNetwork.drawer.comp.dailyLossWithinLimit', 'Daily loss within limit'),
        dailyLossUsed < 100,
        t('managerNetwork.drawer.comp.used', '{{val}}% used', { val: dailyLossUsed })
      ));
    }

    const rawConsistency = drawerAccountInfo?.consistency !== undefined
      ? Number(drawerAccountInfo.consistency)
      : (rep?.consistency_score !== undefined ? Number(rep.consistency_score) : null);

    const consistencyScore = rawConsistency !== null && !isNaN(rawConsistency)
      ? (rawConsistency <= 1 && rawConsistency > 0 ? Math.round(rawConsistency * 100) : Math.round(rawConsistency))
      : null;

    if (consistencyScore !== null && !isNaN(consistencyScore)) {
      comp.push(chk(
        t('managerNetwork.drawer.comp.consistencyRequirement', 'Consistency requirement'),
        consistencyScore >= 50,
        consistencyScore + '/100'
      ));
    }

    // Tìm ngày giao dịch gần nhất từ tất cả các nguồn dữ liệu trade thực tế
    let latestTradeTimestamp: number | null = null;
    if (rep?.last_trade_time) {
      const t = new Date(rep.last_trade_time).getTime();
      if (!isNaN(t)) latestTradeTimestamp = t;
    }

    const tradeLists = [
      ...(Array.isArray(rep?.raw_trades) ? rep.raw_trades : []),
      ...(Array.isArray(rep?.journal_closed_trades) ? rep.journal_closed_trades : []),
      ...(Array.isArray(rep?.closed_trades) ? rep.closed_trades : []),
      ...(Array.isArray(rep?.trades) ? rep.trades : [])
    ];

    for (const tr of tradeLists) {
      const dStr = tr?.close_time || tr?.opentime || tr?.open_time || tr?.time || tr?.date;
      if (dStr) {
        const t = new Date(dStr).getTime();
        if (!isNaN(t) && (latestTradeTimestamp === null || t > latestTradeTimestamp)) {
          latestTradeTimestamp = t;
        }
      }
    }

    if (Array.isArray(rep?.daily_summary)) {
      for (const ds of rep.daily_summary) {
        if (ds?.date) {
          const t = new Date(ds.date).getTime();
          if (!isNaN(t) && (latestTradeTimestamp === null || t > latestTradeTimestamp)) {
            latestTradeTimestamp = t;
          }
        }
      }
    }

    if (Array.isArray(rep?.timeseries_by_trades)) {
      for (const ts of rep.timeseries_by_trades) {
        if (ts?.x) {
          const t = new Date(ts.x).getTime();
          if (!isNaN(t) && (latestTradeTimestamp === null || t > latestTradeTimestamp)) {
            latestTradeTimestamp = t;
          }
        }
      }
    }

    if (latestTradeTimestamp === null) {
      const fallbackStr = rep?.last_updated_time || rep?.date_to || rep?.report_info?.generated_at;
      if (fallbackStr) {
        const t = new Date(fallbackStr).getTime();
        if (!isNaN(t)) latestTradeTimestamp = t;
      }
    }

    if (latestTradeTimestamp !== null) {
      const daysAgo = Math.max(0, Math.floor((now - latestTradeTimestamp) / (1000 * 60 * 60 * 24)));
      comp.push(chk(
        t('managerNetwork.drawer.comp.activeStatus', 'Active status (traded ≤ 7d)'),
        daysAgo <= 7,
        daysAgo === 0 ? t('managerNetwork.drawer.comp.today', 'Today') : t('managerNetwork.drawer.comp.daysAgo', '{{count}}d ago', { count: daysAgo })
      ));
    }
    const hasCompData = comp.length > 0;

    const initial = ((((cmName || '').split(' ')[0]?.[0]) || 'C') + (((cmName || '').split(' ')[1]?.[0]) || '')).toUpperCase();
    const baseRow = da ? rowOf(da) : {
      accountId: drawerId || '',
      cmName,
      alias,
      initial,
      platform: platformTag,
      platformTag: platformTag.toUpperCase(),
      stage,
      stageColor: sc,
      stageBg: sb,
      capital: initialCapital !== null ? money(initialCapital, true) : '—',
      balance: balance !== null ? money(balance, true) : '—',
      pnl: pnl !== null ? (pnl >= 0 ? '+' : '') + money(pnl, true) : '—',
      pnlColor: pnl !== null && pnl >= 0 ? up : down,
      dd: drawdown !== null ? drawdown.toFixed(1) + '%' : '—',
      ddColor: '#1B2430',
      days: tradingDays ?? 0,
      status: st,
      statusColor: STATUS_COLORS[st as keyof typeof STATUS_COLORS] || '#219354',
      onClick: () => { }
    };

    return {
      ...baseRow,
      cmName,
      alias,
      login,
      stage,
      stageColor: sc,
      stageBg: sb,
      network: da?.network || 'BEQ Core',
      stageSource: da?.stageSource || 'API',
      statusColor: STATUS_COLORS[st as keyof typeof STATUS_COLORS] || '#219354',
      toggleBg: on ? '#F2A93B' : '#D5D9DF',
      toggleJustify: on ? 'flex-end' : 'flex-start',
      toggleLabel: on ? t('managerNetwork.drawer.monitoringActive', 'Monitoring active') : t('managerNetwork.drawer.monitoringPaused', 'Monitoring paused'),
      onToggle: () => da ? togglePause(da.accountId) : {},
      classifyLabel: (da?.stage || stage) === 'Unclassified' ? t('managerNetwork.drawer.classifyStage', 'Classify stage') : t('managerNetwork.drawer.reclassifyStage', 'Reclassify stage'),
      onClassify: () => { setModal({ type: 'classify', ids: [da?.accountId || drawerId || ''] }); setDraft({}); },
      tiles,
      risk,
      comp,
      hasTradingData,
      hasRiskData,
      hasCompData,
      payouts: (da?.payouts || []).map(p => ({
        date: formatDate(p.date), amount: money(p.amount), state: p.verified ? t('managerNetwork.drawer.payoutStatus.verified', 'Verified') : t('managerNetwork.drawer.payoutStatus.pending', 'Pending'),
        color: p.verified ? up : '#E09C2F', bg: p.verified ? '#EDF7F1' : '#FFF7EA'
      })),
      history: (da?.history || []).map((h, i) => ({
        stage: h.stage, date: formatDate(h.date), source: h.source,
        dot: (STAGE_STYLE[h.stage as StageType] || ['#666'])[0], line: i < (da?.history || []).length - 1
      })),
      noPayouts: !(da?.payouts?.length),
      noHistory: !(da?.history?.length),
      platformTag,
      initial
    };
  }, [drawerId, da, drawerReport, drawerAccountNumber, drawerAccountInfo, rowOf, togglePause, t]);

  // Filter options
  const filterFields = [
    //{ label: t('managerNetwork.filter.network', 'Network'), value: f.network, options: ['All', 'BEQ Alpha', 'BEQ Delta', 'LUMIR Core'].map(v => ({ v, l: v === 'All' ? t('managerNetwork.filter.allNetworks', 'All networks') : v })), border: f.network !== 'All' ? '#F2A93B' : '#E3E6EA', onChange: (e: any) => setF(prev => ({ ...prev, network: e.target.value })) },
    //{ label: t('managerNetwork.filter.range', 'Range'), value: f.range, options: [['today', t('managerNetwork.filter.today', 'Today')], ['7d', t('managerNetwork.filter.last7Days', 'Last 7 days')], ['30d', t('managerNetwork.filter.last30Days', 'Last 30 days')], ['month', t('managerNetwork.filter.thisMonth', 'This month')], ['lastmonth', t('managerNetwork.filter.lastMonth', 'Last month')], ['custom', t('managerNetwork.filter.custom', 'Custom')]].map(([v, l]) => ({ v, l })), border: f.range !== '30d' ? '#F2A93B' : '#E3E6EA', onChange: (e: any) => setF(prev => ({ ...prev, range: e.target.value })) },

    { label: t('managerNetwork.filter.network', 'Network'), value: f.network, options: ['All'].map(v => ({ v, l: v === 'All' ? t('managerNetwork.filter.allNetworks', 'All networks') : v })), border: f.network !== 'All' ? '#F2A93B' : '#E3E6EA', onChange: (e: any) => setF(prev => ({ ...prev, network: e.target.value })) },
    { label: t('managerNetwork.filter.range', 'Range'), value: f.range, options: [['30d', t('managerNetwork.filter.last30Days', 'Last 30 days')]].map(([v, l]) => ({ v, l })), border: f.range !== '30d' ? '#F2A93B' : '#E3E6EA', onChange: (e: any) => setF(prev => ({ ...prev, range: e.target.value })) },
    { label: t('managerNetwork.filter.platform', 'Platform'), value: f.platform, options: ['All', 'MT5', 'MT4', 'cTrader'].map(v => ({ v, l: v === 'All' ? t('managerNetwork.filter.allPlatforms', 'All platforms') : v })), border: f.platform !== 'All' ? '#F2A93B' : '#E3E6EA', onChange: (e: any) => setF(prev => ({ ...prev, platform: e.target.value })) },
    { label: t('managerNetwork.filter.stage', 'Stage'), value: f.stage, options: ['All', 'Developing', 'Challenge', 'Verified', 'Funded'].map(v => ({ v, l: v === 'All' ? t('managerNetwork.filter.allStages', 'All stages') : t('managerNetwork.stages.' + v, v) })), border: f.stage !== 'All' ? '#F2A93B' : '#E3E6EA', onChange: (e: any) => setF(prev => ({ ...prev, stage: e.target.value })) },
    { label: t('managerNetwork.filter.status', 'Status'), value: f.status, options: ['All', 'Normal', 'Attention', 'Paused', 'Recovery', 'Scale Ready'].map(v => ({ v, l: v === 'All' ? t('managerNetwork.filter.allStatuses', 'All statuses') : t('managerNetwork.statuses.' + v, v) })), border: f.status !== 'All' ? '#F2A93B' : '#E3E6EA', onChange: (e: any) => setF(prev => ({ ...prev, status: e.target.value })) }
  ];

  const sortOptions = [
    { v: 'initialCapital', l: t('managerNetwork.sort.capital', 'Capital (high to low)') },
    { v: 'balance', l: t('managerNetwork.sort.balance', 'Balance (high to low)') },
    { v: 'pnl', l: t('managerNetwork.sort.pnl', 'P&L (high to low)') },
    { v: 'drawdown', l: t('managerNetwork.sort.drawdown', 'Drawdown (low risk first)') },
    { v: 'cmName', l: t('managerNetwork.sort.cmName', 'CM name (A-Z)') },
    { v: 'tradingDays', l: t('managerNetwork.sort.tradingDays', 'Trading days') },
    { v: 'status', l: t('managerNetwork.sort.status', 'Status') }
  ];

  const activeFilterCount = (f.network !== 'All' ? 1 : 0) + (f.range !== '30d' ? 1 : 0) + (f.platform !== 'All' ? 1 : 0) + (f.stage !== 'All' ? 1 : 0) + (f.status !== 'All' ? 1 : 0);

  // Drilldown list
  const isStatDetail = Boolean(modal && modal.key && modal.key.startsWith('stat:'));

  const statDetailRows = useMemo(() => {
    return statDetailAccounts.map(item => {
      const [sc, sb] = STAGE_STYLE[item.stage as StageType] || ['#2563EB', '#EFF6FF'];
      const stColor = item.status_color === 'green' ? '#219354' : item.status_color === 'red' ? '#D9483B' : '#E09C2F';
      const pnlIsPos = typeof item.pnl_text === 'string' && item.pnl_text.startsWith('+');
      const pnlIsNeg = typeof item.pnl_text === 'string' && item.pnl_text.startsWith('-');
      const pColor = pnlIsPos ? up : pnlIsNeg ? down : '#1B1B1B';

      return {
        accountId: item.account_meta ? item.account_meta.split('·')[0].trim() : String(item.id),
        cmName: item.account_name,
        alias: item.account_meta ? item.account_meta.split('·')[1]?.trim() || item.platform : item.platform,
        platform: item.platform,
        stage: item.stage,
        stageColor: sc,
        stageBg: sb,
        capital: item.capital_text,
        balance: item.balance_text,
        pnl: item.pnl_text,
        pnlColor: pColor,
        dd: item.dd,
        ddColor: item.dd && item.dd.startsWith('-') && parseFloat(item.dd) <= -5 ? down : '#1B2430',
        status: item.status,
        statusColor: stColor,
        onClick: () => {
          const accNum = item.account_meta ? item.account_meta.split('·')[0].trim() : String(item.id);
          const match = accounts.find(a =>
            (a.cmName && item.account_name && a.cmName.toLowerCase() === item.account_name.toLowerCase()) ||
            (item.account_meta && a.login === accNum)
          );
          if (match) {
            setDrawerId(match.accountId);
            setDrawerAccountNumber(accNum);
          } else if (accounts[0]) {
            setDrawerId(accounts[0].accountId);
            setDrawerAccountNumber(accNum);
          } else {
            setDrawerId(accNum);
            setDrawerAccountNumber(accNum);
          }
          setDrawerAccountInfo({
            cmName: item.account_name,
            alias: item.account_meta ? item.account_meta.split('·')[1]?.trim() || item.platform : item.platform,
            login: accNum,
            platform: item.platform,
            stage: item.stage,
            capital: item.capital,
            balance: item.balance,
            drawdown: item.dd ? parseFloat(item.dd) : undefined
          });
        }
      };
    });
  }, [statDetailAccounts, accounts, up, down]);

  const listRows = useMemo(() => {
    if (!modal || modal.type !== 'list' || !modal.key) return [];
    if (isStatDetail) return statDetailRows;
    const pred = getPred(modal.key, period.end);
    let rows = accounts.filter(a => pred(a) && matchesSearch(a, listSearch));
    if (modal.sort === 'drawdown') rows = rows.slice().sort((a, b) => a.drawdown - b.drawdown);
    return rows.map(a => rowOf(a));
  }, [modal, isStatDetail, statDetailRows, accounts, getPred, period.end, listSearch, matchesSearch, rowOf]);

  // Performance table rows
  const perfRows = useMemo(() => {
    return (perfData?.ser || []).map((x, i) => ({
      label: x.label,
      newCM: x.newCM,
      growth: x.growth.toFixed(1) + '%',
      newFunded: x.newFunded,
      totalFunded: x.totalFunded,
      fundedCap: compact(x.fundedCap),
      payout: money(x.payout),
      payoutCMs: x.paid,
      bg: modal && modal.month === i ? '#FFF7EA' : '#fff'
    }));
  }, [perfData, modal]);

  // Access rows
  const aq = accessSearch.trim().toLowerCase();
  const toggleIds = useCallback((ids: string[]) => {
    setAccessSel(st => {
      const next = { ...st };
      const all = ids.every(id => next[id]);
      ids.forEach(id => {
        if (all) delete next[id];
        else next[id] = true;
      });
      return next;
    });
  }, []);

  const accessRows = useMemo(() => {
    const umap: Record<string, { userId: string; name: string; email: string; created: number; accs: AccountData[]; inN?: number }> = {};
    accounts.forEach(a => {
      (umap[a.userId] = umap[a.userId] || { userId: a.userId, name: a.cmName, email: a.email, created: a.requestedAt, accs: [] }).accs.push(a);
    });
    const users = Object.values(umap);
    users.forEach(u => { u.inN = u.accs.filter(a => a.approved).length; });

    const accessAll = users.filter(u =>
      (accessTab === 'all' || (accessTab === 'in' ? (u.inN || 0) > 0 : (u.inN || 0) < u.accs.length)) &&
      (!aq || u.userId.toLowerCase().includes(aq) || u.email.toLowerCase().includes(aq) || u.accs.some(a => a.login.includes(aq) || a.accountId.toLowerCase().includes(aq)))
    ).sort((x, y) => ((x.inN || 0) / x.accs.length - (y.inN || 0) / y.accs.length) || x.userId.localeCompare(y.userId));

    return accessAll.map(u => {
      const ids = u.accs.map(a => a.accountId);
      const nSel = ids.filter(id => accessSel[id]).length;
      const hit = aq ? u.accs.filter(a => a.login.includes(aq) || a.accountId.toLowerCase().includes(aq)) : [];
      const expanded = accessExp[u.userId] ?? (hit.length > 0 && u.accs.length > 1);
      const inCount = u.inN || 0;
      const st = inCount === 0 ? [t('managerNetwork.accessModal.statuses.notAdded', 'Not added'), '#4B5563', '#F1F2F5'] :
        inCount === u.accs.length ? [t('managerNetwork.accessModal.statuses.inNetwork', 'In network'), '#1F7A6E', '#DDF1EE'] :
          [t('managerNetwork.accessModal.statuses.partial', 'Partial {{inCount}}/{{total}}', { inCount, total: u.accs.length }), '#A8660B', '#FFF1DA'];

      return {
        userId: u.userId,
        name: u.name,
        email: u.email,
        accCount: u.accs.length > 1
          ? t('managerNetwork.accessModal.accountsCount', '{{count}} accounts', { count: u.accs.length })
          : t('managerNetwork.accessModal.accountCount', '{{count}} account', { count: u.accs.length }),
        logins: (hit.length ? hit : u.accs).map(a => a.login).join(', '),
        loginColor: hit.length ? '#8C6424' : '#6B7280',
        requested: formatDate(u.created),
        status: st[0],
        stColor: st[1],
        stBg: st[2],
        bg: nSel ? '#FFFCF7' : '#fff',
        boxBorder: nSel ? '#B8893A' : '#C9CED6',
        boxBg: nSel ? '#B8893A' : '#fff',
        boxIcon: nSel && nSel < ids.length ? 'minus' : 'check',
        expanded,
        chev: expanded ? 'down' : 'right',
        onExpand: (e: React.MouseEvent) => {
          e.stopPropagation();
          setAccessExp(s2 => ({ ...s2, [u.userId]: !expanded }));
        },
        onToggle: () => toggleIds(ids),
        accs: u.accs.map(a => {
          const on = !!accessSel[a.accountId];
          const isHit = hit.includes(a);
          return {
            login: a.login,
            platform: a.platform,
            capital: money(a.initialCapital),
            accountId: a.accountId,
            alias: a.alias,
            status: a.approved ? t('managerNetwork.accessModal.statuses.inNetwork', 'In network') : t('managerNetwork.accessModal.statuses.notAdded', 'Not added'),
            stColor: a.approved ? '#1F7A6E' : '#4B5563',
            stBg: a.approved ? '#DDF1EE' : '#F1F2F5',
            onToggle: () => toggleIds([a.accountId]),
            border: on ? '#F2A93B' : '#E6E8EC',
            bg: on ? '#FFFDF8' : '#FAFBFB',
            boxBorder: on ? '#B8893A' : '#C9CED6',
            boxBg: on ? '#B8893A' : '#fff',
            loginColor: isHit ? '#8C6424' : '#1B2430'
          };
        })
      };
    });
  }, [accounts, accessTab, aq, accessSel, accessExp, toggleIds, t]);

  const selAccs = accounts.filter(a => accessSel[a.accountId]);
  const approveCount = selAccs.filter(a => !a.approved).length;
  const revokeCount = selAccs.filter(a => a.approved).length;
  const allSel = accessRows.length > 0 && accessRows.every(u => u.accs.every(a => accessSel[a.accountId]));
  const someSel = accessRows.some(u => u.accs.some(a => accessSel[a.accountId]));

  const setAccess = (approved: boolean) => {
    const ids = Object.keys(accessSel).filter(k => accessSel[k]);
    if (!ids.length) return toastMsg(t('managerNetwork.toasts.selectAtLeastOneAccount', 'Select at least one account'));
    setAccounts(prev => prev.map(a => {
      if (!accessSel[a.accountId]) return a;
      return { ...a, approved, access: approved ? 'In network' : 'Not added' };
    }));
    setAccessSel({});
    toastMsg(approved
      ? t('managerNetwork.toasts.accountsAdded', '{{count}} account(s) added to network', { count: ids.length })
      : t('managerNetwork.toasts.accountsRevoked', '{{count}} account(s) revoked', { count: ids.length })
    );
  };

  // Classify rows
  const classifyRows = (modal && modal.ids ? accounts.filter(a => modal.ids!.includes(a.accountId)) : unclassifiedAll).map(a => {
    const perf = (a.pnl / a.initialCapital) * 100;
    const st = statusOf(a);
    const v = draft[a.accountId] || '';
    return {
      accountId: a.accountId,
      cmName: a.cmName,
      alias: a.alias,
      platform: a.platform,
      capital: money(a.initialCapital),
      balance: money(a.balance, true),
      perf: (perf >= 0 ? '+' : '') + perf.toFixed(2) + '%',
      perfColor: perf >= 0 ? up : down,
      dd: a.drawdown.toFixed(1) + '%',
      ddColor: a.drawdown <= -5 ? '#C27C12' : '#1B2430',
      status: st,
      statusColor: STATUS_COLORS[st],
      value: v,
      border: v ? '#F2A93B' : '#E3E6EA',
      bg: v ? '#FFFCF7' : '#fff',
      onChange: (e: any) => setDraft(prev => ({ ...prev, [a.accountId]: e.target.value })),
      options: ['', 'Demo', 'Challenge', 'Verified', 'Funded'].map(o => ({
        v: o,
        l: o ? t('managerNetwork.stages.' + o, o) : t('managerNetwork.classifyModal.selectStage', 'Select stage…')
      }))
    };
  });

  const draftCount = Object.values(draft).filter(Boolean).length;

  const saveClassification = () => {
    const ids = Object.keys(draft).filter(k => draft[k]);
    if (!ids.length) return toastMsg(t('managerNetwork.toasts.selectStageForAtLeastOne', 'Select a stage for at least one account'));
    const tTime = Date.now();
    setAccounts(prev => prev.map(a => {
      if (!draft[a.accountId]) return a;
      const stage = draft[a.accountId] as StageType;
      return {
        ...a,
        stage,
        stageSource: 'Admin',
        history: [...a.history, { stage, date: tTime, source: 'Admin' }],
        fundedDate: stage === 'Funded' ? (a.fundedDate || tTime) : a.fundedDate
      };
    }));
    setModal(null);
    setDraft({});
    toastMsg(t('managerNetwork.toasts.accountsClassified', '{{count}} account(s) classified', { count: ids.length }));
  };

  const refreshData = async () => {
    if (refreshing) return;
    setRefreshing(true);
    await Promise.all([loadDashboardData(), loadTopManagersData()]);
    setRefreshing(false);
    setLastUpdated(Date.now());
    toastMsg(t('managerNetwork.toasts.tradingDataSynced', 'Trading data synced from FTMO API'));
  };

  // Top sort options
  const topSortOpts = topCols.map(c => ({
    v: c.key,
    l: t('managerNetwork.top.sortPrefix', 'Sort: {{label}}', { label: c.label })
  }));
  const onTopSort = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const k = e.target.value;
    setTopSort({ key: k, dir: k === 'cmId' ? 'asc' : 'desc' });
    setExpanded(false);
  };

  const diffTime = Math.floor((now - lastUpdated) / 1000);
  const lastUpdatedText = diffTime < 60
    ? t('managerNetwork.header.justNow', 'Just now')
    : diffTime < 3600
      ? t('managerNetwork.header.minAgo', '{{count}} min ago', { count: Math.floor(diffTime / 60) })
      : formatDate(lastUpdated);

  // Template bind values
  const heroPad = mw < 640 ? '16px' : '18px 24px';
  const heroSize = mw < 480 ? '21px' : mw < 900 ? '27px' : '34px';
  const heroAlign = mw < 760 ? 'flex-start' : 'flex-end';
  const heroText = mw < 760 ? 'left' : 'right';
  const heroRightW = mw < 760 ? '100%' : 'auto';
  const heroSelW = mw < 760 ? '100%' : 'auto';
  const heroSelWrap = mw < 760 ? 'wrap' : 'nowrap';
  const heroSelFlexA = mw < 760 ? '1 1 180px' : '0 0 190px';
  const heroSelFlexB = mw < 760 ? '1 1 150px' : '0 0 160px';
  const heroQuoteDisp = mw < 760 ? 'none' : 'flex';
  const jGrid = jNarrow ? 'repeat(3,minmax(0,1fr))' : 'repeat(6,minmax(100px,1fr))';
  const jGap = jNarrow ? '6px' : '3px';
  const jMin = jNarrow ? '0' : '640px';
  const jRadius = jNarrow ? '8px' : '0';
  const topCards = mw < 700;
  const topTable = mw >= 700;
  const moreH = mw < 700 ? '44px' : '32px';
  const topSortKey = topSort.key;
  const todayText = new Date(now).toLocaleDateString(i18n.language === 'vi' ? 'vi-VN' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).replace(/^./, c => c.toUpperCase());
  const timeText = new Date().toLocaleTimeString('en-GB');
  const openAllCM = () => openStatList('total_cms', t('managerNetwork.drilldown.capitalManagers', 'Capital Managers'), t('managerNetwork.drilldown.allClassifiedCMs', 'All classified Capital Managers.'));
  const row2Cols = mw >= 1180 ? 'minmax(0,1.8fr) minmax(0,1fr) minmax(0,120px)' : mw >= 900 ? 'minmax(0,1.6fr) minmax(0,1fr)' : '1fr';
  const row3Cols = mw >= 1180 ? 'minmax(0,1.25fr) minmax(0,1.05fr) minmax(0,0.95fr)' : mw >= 760 ? 'minmax(0,1fr) minmax(0,1fr)' : '1fr';
  const showQuote = mw >= 1180;
  const kiSpan = mw >= 1180 || mw < 760 ? 'auto' : 'span 2';
  const kpiValSize = mw >= 1300 ? '20px' : mw >= 1050 ? '17px' : mw >= 768 ? '15px' : mw >= 540 ? '13px' : '16px';
  const pagePad = isMobile ? '12px' : w < 1024 ? '16px 20px' : '24px 32px';
  const hdrNet = filterFields[0];
  const hdrRange = filterFields[1];
  const periodText = `${formatDate(period.start)} – ${formatDate(period.end)}`;
  const refresh = refreshData;
  const refreshColor = refreshing ? '#F2A93B' : '#1B2430';
  const refreshLabel = refreshing ? t('managerNetwork.header.syncing', 'Syncing…') : t('managerNetwork.header.refresh', 'Refresh');
  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => { setSearch(e.target.value); setExpanded(false); };
  useEffect(() => {
    const q = search.trim();
    const id = setTimeout(() => setTopQuery(q), q ? 400 : 0);
    return () => clearTimeout(id);
  }, [search]);
  const toggleFilters = () => setShowFilters(s => !s);
  const filterBtnBorder = showFilters || activeFilterCount ? '#F2A93B' : '#E3E6EA';
  const hasActiveFilters = activeFilterCount > 0;
  const onSort = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setSortKey(val);
    if (val === 'initialCapital') setTopSort({ key: 'capital', dir: 'desc' });
    else if (val === 'cmName') setTopSort({ key: 'cmId', dir: 'asc' });
    else if (val === 'score') setTopSort({ key: 'score', dir: 'desc' });
  };
  const resetFilters = () => { setF(defaultF); setSearch(''); setExpanded(false); toastMsg(t('managerNetwork.toasts.filtersReset', 'Filters reset')); };
  const openAccess = () => { setModal({ type: 'access' }); setAccessSel({}); setAccessExp({}); setAccessSearch(''); setAccessTab('out'); };
  const hasUnclassified = unclassifiedCount > 0;
  const openClassify = () => { setModal({ type: 'classify' }); setDraft({}); };
  const isCustom = f.range === 'custom';
  const onFrom = (e: React.ChangeEvent<HTMLInputElement>) => setF(prev => ({ ...prev, from: e.target.value }));
  const onTo = (e: React.ChangeEvent<HTMLInputElement>) => setF(prev => ({ ...prev, to: e.target.value }));
  const journeyBase = base;
  const fundedCapital = cumTotalFunded !== undefined && cumTotalFunded !== null
    ? Number(cumTotalFunded)
    : (dashboardData?.overview_cards?.total_funded_capital?.value !== undefined
      ? Number(dashboardData.overview_cards.total_funded_capital.value)
      : cur.fundedCapital);
  const fundedCapitalText = money(fundedCapital).replace('$', '$ ');
  const fundedCount = cumAccountCount !== undefined && cumAccountCount !== null
    ? Number(cumAccountCount)
    : (dashboardData?.overview_cards?.funded?.value !== undefined
      ? Number(dashboardData.overview_cards.funded.value)
      : cur.funded);
  const avgTicketVal = cumAvgSize !== undefined && cumAvgSize !== null
    ? Number(cumAvgSize)
    : (fundedCount > 0 ? fundedCapital / fundedCount : 0);
  const avgTicket = compact(avgTicketVal);
  const openPerf = () => setModal({ type: 'perf', month: null });
  const perfTabs = [
    { label: t('managerNetwork.perf.tabs.funded', 'Funded CM'), bg: perfTab === 'funded' ? '#B8893A' : '#F3EEE5', color: perfTab === 'funded' ? '#fff' : '#3A342C', onClick: () => setPerfTab('funded') },
    { label: t('managerNetwork.perf.tabs.payout', 'Payout'), bg: perfTab === 'payout' ? '#B8893A' : '#F3EEE5', color: perfTab === 'payout' ? '#fff' : '#3A342C', onClick: () => setPerfTab('payout') },
    //{ label: t('managerNetwork.perf.tabs.newcm', 'New CM'), bg: perfTab === 'newcm' ? '#B8893A' : '#F3EEE5', color: perfTab === 'newcm' ? '#fff' : '#3A342C', onClick: () => setPerfTab('newcm') }
  ];
  const perfBarLabel = perfData.barLabel;
  const perfLineLabel = perfData.lineLabel;
  const currentLoaded = topManagersApiData ? topManagersApiData.length : 0;
  const totalCms = topManagersMeta?.total_cms ?? currentLoaded;
  const tableTotal = totalCms;
  const noRows = rows.length === 0;

  // The full list is already loaded (for the ranking); "Show more" only reveals the next 10 rows
  const shownCount = Math.min(topVisible, currentLoaded);
  const hasMore = shownCount < currentLoaded;

  const moreLabel = t('managerNetwork.top.showMoreWithCount', 'Show more ({{current}}/{{total}})', { current: shownCount, total: currentLoaded });

  const toggleMore = useCallback(() => {
    setTopVisible(v => v + TOP_PAGE_SIZE);
  }, []);
  const closeModal = () => {
    setModal(null);
    setDraft({});
    setAccessSel({});
    setStatDetailAccounts([]);
    setStatDetailTotal(0);
    setStatLoading(false);
    statReqIdRef.current++;
  };
  const listOpen = Boolean(modal && modal.type === 'list');
  const listTitle = modal?.title || '';
  const listSub = modal?.sub || '';
  const listCount = isStatDetail ? statDetailTotal : listRows.length;
  const onListSearch = (e: React.ChangeEvent<HTMLInputElement>) => setListSearch(e.target.value);
  const perfOpen = Boolean(modal && modal.type === 'perf');
  const accessOpen = Boolean(modal && modal.type === 'access');
  const cnt3 = useMemo(() => {
    const umap: Record<string, { inN: number; total: number }> = {};
    accounts.forEach(a => {
      umap[a.userId] = umap[a.userId] || { inN: 0, total: 0 };
      umap[a.userId].total++;
      if (a.approved) umap[a.userId].inN++;
    });
    const list = Object.values(umap);
    return {
      out: list.filter(u => u.inN === 0).length,
      in: list.filter(u => u.inN === u.total).length,
      all: list.length
    };
  }, [accounts]);

  const accessTabs = [
    { label: t('managerNetwork.accessModal.tabs.notAdded', 'Not added ({{count}})', { count: cnt3.out }), bg: accessTab === 'out' ? '#B8893A' : '#fff', color: accessTab === 'out' ? '#fff' : '#3A342C', onClick: () => setAccessTab('out') },
    { label: t('managerNetwork.accessModal.tabs.inNetwork', 'In network ({{count}})', { count: cnt3.in }), bg: accessTab === 'in' ? '#B8893A' : '#fff', color: accessTab === 'in' ? '#fff' : '#3A342C', onClick: () => setAccessTab('in') },
    { label: t('managerNetwork.accessModal.tabs.all', 'All users ({{count}})', { count: cnt3.all }), bg: accessTab === 'all' ? '#B8893A' : '#fff', color: accessTab === 'all' ? '#fff' : '#3A342C', onClick: () => setAccessTab('all') }
  ];
  const onAccessSearch = (e: React.ChangeEvent<HTMLInputElement>) => setAccessSearch(e.target.value);
  const toggleAllAccess = () => {
    setAccessSel(st => {
      const next = { ...st };
      accessRows.forEach(u => {
        u.accs.forEach(a => {
          if (allSel) delete next[a.accountId];
          else next[a.accountId] = true;
        });
      });
      return next;
    });
  };
  const allBorder = someSel ? '#B8893A' : '#C9CED6';
  const allBg = someSel ? '#B8893A' : '#fff';
  const allIcon = allSel ? 'check' : someSel ? 'minus' : 'check';
  const accessSelCount = selAccs.length;
  const accessSelUsers = new Set(selAccs.map(a => a.userId)).size;
  const approveBg = approveCount ? '#B8893A' : '#DCC9A4';
  const revokeBorder = revokeCount ? '#F3B8B2' : '#E3E6EA';
  const revokeColor = revokeCount ? '#B42318' : '#9AA3AF';
  const approveSelected = () => setAccess(true);
  const revokeSelected = () => setAccess(false);
  const accessEmpty = accessRows.length === 0;
  const classifyOpen = Boolean(modal && modal.type === 'classify');
  const classifyTitle = modal && modal.ids
    ? t('managerNetwork.classifyModal.titleAccount', 'Classify account stage')
    : t('managerNetwork.classifyModal.titleReview', 'Review unclassified accounts ({{count}})', { count: classifyRows.length });
  const saveBg = draftCount ? '#F2A93B' : '#F5D9A8';
  const closeDrawer = () => {
    setDrawerId(null);
    setDrawerAccountNumber(null);
    setDrawerAccountInfo(null);
    setDrawerReport(null);
  };
  const showTags = true;

  return (
    <Layout contentClassName="p-0">
      <Helmet>
        <title>{t('managerNetwork.pageTitle', 'BEQ Capital Manager Network Dashboard')}</title>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,500&display=swap" />
      </Helmet>

      <div className="capital-manager-network-dashboard" style={s('min-height:100vh;background:#F4F4F4;display:flex;flex-direction:column;width:100%')}>
        <div style={s(`display:flex;gap:24px;padding:${pagePad};align-items:flex-start;flex:1`)}>
          <main ref={mainRef} style={s(`flex:1;min-width:0;display:flex;flex-direction:column;gap:16px`)}>
            <HeroHeader
              hdrNet={hdrNet}
              hdrRange={hdrRange}
              heroPad={heroPad}
              heroSize={heroSize}
              heroAlign={heroAlign}
              heroText={heroText}
              heroRightW={heroRightW}
              heroSelW={heroSelW}
              heroSelWrap={heroSelWrap}
              heroSelFlexA={heroSelFlexA}
              heroSelFlexB={heroSelFlexB}
              heroQuoteDisp={heroQuoteDisp}
              todayText={todayText}
              timeText={timeText}
              periodText={periodText}
              lastUpdatedText={lastUpdatedText}
              refresh={refresh}
              refreshColor={refreshColor}
              refreshLabel={refreshLabel}
            />

            <DashboardToolbar
              search={search}
              onSearch={onSearch}
              toggleFilters={toggleFilters}
              filterBtnBorder={filterBtnBorder}
              hasActiveFilters={hasActiveFilters}
              activeFilterCount={activeFilterCount}
              sortKey={sortKey}
              onSort={onSort}
              sortOptions={sortOptions}
              refresh={refresh}
              resetFilters={resetFilters}
              openAccess={openAccess}
              hasUnclassified={hasUnclassified}
              openClassify={openClassify}
            />

            <FilterPanel
              showFilters={showFilters}
              filterFields={filterFields}
              isCustom={isCustom}
              from={f.from}
              to={f.to}
              onFrom={onFrom}
              onTo={onTo}
            />

            <UnclassifiedAlert
              unclassifiedCount={unclassifiedCount}
              openClassify={openClassify}
            />

            <KpiCards
              kpis={kpis}
              kpiCols={kpiCols}
              kpiValSize={kpiValSize}
            />

            <JourneyNetworkSection
              row2Cols={row2Cols}
              journeyBase={journeyBase}
              statusCenter={accountStatus
                ? { value: accountStatus.total, unit: t('managerNetwork.journey.accountsUnit', 'accounts') }
                : { value: journeyBase, unit: 'CM' }}
              openAllCM={openAllCM}
              jGrid={jGrid}
              jGap={jGap}
              jMin={jMin}
              jRadius={jRadius}
              journey={journey}
              donutBg={donutBg}
              statusList={statusList}
              showQuote={showQuote}
            />

            <div style={s(`display:grid;grid-template-columns:${row3Cols};gap:12px`)}>
              <CapitalUnderManagement
                fundedCapitalText={fundedCapitalText}
                fundedCount={fundedCount}
                avgTicket={avgTicket}
                tiers={tiers}
              />

              <PerformanceChart
                openPerf={openPerf}
                perfTabs={perfTabs}
                perfRef={perfRef}
                perfBarLabel={perfBarLabel}
                perfLineLabel={perfLineLabel}
              />

              <KeyIndicators
                kiSpan={kiSpan}
                keyInd={keyInd}
              />
            </div>

            <TopManagersSection
              tableTotal={tableTotal}
              topCards={topCards}
              topTable={topTable}
              topSortKey={topSortKey}
              onTopSort={onTopSort}
              topSortOpts={topSortOpts}
              topCols={topCols}
              rankTip={topColTips.rank}
              rows={rows}
              noRows={noRows}
              hasMore={hasMore}
              moreH={moreH}
              moreLabel={moreLabel}
              toggleMore={toggleMore}
              selectedTierCapital={selectedTierCapital}
              onClearTierFilter={() => onSelectTierCapital(selectedTierCapital || 0)}
            />
          </main>
        </div>

        <ListDrilldownModal
          isOpen={listOpen}
          closeModal={closeModal}
          title={listTitle}
          sub={listSub}
          count={listCount}
          search={isStatDetail ? statSearch : listSearch}
          onSearch={isStatDetail ? onStatSearch : onListSearch}
          rows={listRows}
          loading={isStatDetail ? statLoading : false}
          sortBy={isStatDetail ? statSortBy : undefined}
          order={isStatDetail ? statOrder : undefined}
          onSortByChange={isStatDetail ? onStatSortByChange : undefined}
          onOrderToggle={isStatDetail ? onStatOrderToggle : undefined}
          page={isStatDetail ? statPage : 1}
          lastPage={isStatDetail ? statDetailLastPage : 1}
          onPageChange={isStatDetail ? onStatPageChange : undefined}
        />

        <PerformanceDetailModal
          isOpen={perfOpen}
          closeModal={closeModal}
          perfRows={perfRows}
        />

        <AccessManagementModal
          isOpen={accessOpen}
          closeModal={closeModal}
          accessTabs={accessTabs}
          accessSearch={accessSearch}
          onAccessSearch={onAccessSearch}
          toggleAllAccess={toggleAllAccess}
          allBorder={allBorder}
          allBg={allBg}
          allIcon={allIcon}
          accessRows={accessRows}
          accessEmpty={accessEmpty}
          accessSelCount={accessSelCount}
          accessSelUsers={accessSelUsers}
          revokeSelected={revokeSelected}
          revokeBorder={revokeBorder}
          revokeColor={revokeColor}
          revokeCount={revokeCount}
          approveSelected={approveSelected}
          approveBg={approveBg}
          approveCount={approveCount}
        />

        <StageClassifyModal
          isOpen={classifyOpen}
          closeModal={closeModal}
          title={classifyTitle}
          classifyRows={classifyRows}
          draftCount={draftCount}
          saveBg={saveBg}
          saveClassification={saveClassification}
        />

        <AccountDetailDrawer
          drawerData={d}
          closeDrawer={closeDrawer}
          drawerRef={drawerRef}
          showTags={showTags}
          loading={loadingDrawerReport}
          hasChartData={drawerChartInfo.hasData}
          chartSummary={drawerChartInfo.summary}
        />

        <ToastNotification toast={toast} />
      </div>
    </Layout>
  );
};

export default ManagerNetworkDashboard;
